// Format rendering engine — shared by all clause pages.
// Extracted from App.js so each deployment prompt stays under size limits.
import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { api } from "./api";
import { CLAUSE_TITLES, CLAUSE_FORMATS } from "./formats";

export const inDate = (s) => { const m = typeof s === "string" && s.match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? m[3] + "-" + m[2] + "-" + m[1] : s; };

// Minimal RFC-4180 CSV parser — handles quoted fields, escaped quotes, and newlines inside quotes.
function parseCSV(text) {
  const rows = []; let row = []; let field = ""; let inQ = false;
  text = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else { inQ = false; } }
      else { field += ch; }
    } else if (ch === '"') { inQ = true; }
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else { field += ch; }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}
// Normalise a date cell to ISO YYYY-MM-DD (accepts DD-MM-YYYY / DD.MM.YYYY / DD/MM/YYYY and ISO).
function toISODate(v) {
  v = String(v || "").trim();
  let m = v.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return m[3] + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0");
  m = v.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) return m[1] + "-" + m[2].padStart(2, "0") + "-" + m[3].padStart(2, "0");
  return v;
}

function printFormat(id) {
  const el = document.getElementById(id);
  if (!el) return;
  // Print into a fresh window from a CLONE. Live form controls (textarea/input) render as
  // fixed-height boxes with internal scrollbars in print — which clips long paragraphs. Replace
  // them with static, naturally-wrapping text so every row expands to fit its full content and
  // nothing is cut off. Values are read from the LIVE nodes (a clone doesn't carry typed values).
  const clone = el.cloneNode(true);
  const liveTA = el.querySelectorAll("textarea"), cloneTA = clone.querySelectorAll("textarea");
  liveTA.forEach((t, i) => {
    const c = cloneTA[i]; if (!c || !c.parentNode) return;
    const div = document.createElement("div");
    div.textContent = t.value;
    div.setAttribute("style", "white-space:pre-wrap;overflow-wrap:break-word;word-break:break-word;");
    c.parentNode.replaceChild(div, c);
  });
  const liveIn = el.querySelectorAll("input"), cloneIn = clone.querySelectorAll("input");
  liveIn.forEach((inp, i) => {
    const c = cloneIn[i]; if (!c || !c.parentNode) return;
    const t = (inp.getAttribute("type") || "text").toLowerCase();
    if (t === "file" || t === "checkbox" || t === "radio") return;   // leave non-text controls as-is
    const span = document.createElement("span");
    span.textContent = inp.value;
    c.parentNode.replaceChild(span, c);
  });
  const w = window.open("", "_blank", "width=920,height=760");
  w.document.write('<html><head><title>Sanber Labs QMS</title><style>body{font-family:Georgia,"Times New Roman",serif;color:#111;padding:28px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #333;padding:6px 9px;font-size:13px;text-align:left;vertical-align:top;height:auto;overflow:visible;overflow-wrap:break-word;word-break:break-word}th{background:#eee}.fmt-head{text-align:center;position:relative;margin-bottom:8px}.fmt-lab{font-weight:700;font-size:16px}.fmt-code{position:absolute;right:0;top:0;font-size:12px}.fmt-title{font-weight:700;font-size:18px;margin-top:6px;text-decoration:underline}.fmt-footer{margin-top:36px;font-weight:600}.noprint{display:none}img{max-height:52px}ol{line-height:1.7}.fmt-sign{display:inline-block;width:45%;margin-top:34px;vertical-align:top}.sign-line{border-top:1px solid #333;width:190px;height:38px;margin-bottom:4px}body{padding-top:48px}.ctrl-stamp{position:fixed;top:6px;right:12px;color:#c00;border:2.5px solid #c00;padding:2px 14px;font-family:Arial,sans-serif;font-weight:700;font-size:13px;letter-spacing:3px;transform:rotate(-6deg);border-radius:4px;opacity:.85;z-index:99}</style></head><body><div class="ctrl-stamp">CONTROLLED</div>' + clone.innerHTML + '</body></html>');
  w.document.close();
  w.focus();
  setTimeout(function () { w.print(); }, 400);
}

// Build the full paginated HTML for a manual: a paged.js document whose running control box sits
// at the BOTTOM of every page (@bottom-center) with per-section metadata + document-level
// signatures and the correct page number. Shared verbatim by the Print/PDF window and the
// on-screen page-by-page preview so screen and print are the same engine and the same data.
// opts.preview → inline iframe styling + posts its height to the parent (no auto-print).
function buildPagedHtml(title, code, html, footerMeta, opts) {
  opts = opts || {};
  const preview = !!opts.preview;
  const esc = function (s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
  const fm = footerMeta || {};
  // Cover page (page 1) of a controlled manual carries its own copy-control block + CONTROLLED COPY
  // stamp, so it must NOT show the running document-control footer or the top-right CONTROLLED mark.
  const coverNoFooter = !!fm.coverNoFooter;
  const docNo = fm.docNo || String(code || "").replace(/SAN-?Lab/i, "SANLAB");
  const docName = (fm.docName || String(title || "")).toUpperCase();
  const issueNo = fm.issueNo || "01", issueDate = fm.issueDate || "05/10/2024";
  const amendNo = fm.amendNo || "00", amendDate = fm.amendDate || "";
  // Per-section header metadata (keyed by heading id) + document-level signatures come from
  // the stored document header. Missing values fall back to the document defaults above so the
  // running header is always complete, even for other manuals that have no header saved yet.
  const secMeta = fm.sections || {};
  const sigs = fm.signatures || {};
  // Tag each heading with the section's control values. paged.js `string-set` carries each value
  // forward onto every physical page of that section (including continuation pages) until the
  // next heading changes it — so the running header shows the right Section / Issue / Amend per page.
  let bodyHtml = html || "";
  try {
    const pdoc = new DOMParser().parseFromString(html || "", "text/html");
    const CODE_RE = /^\s*([0-9]+(?:\.[0-9]+)*(?:\s*[FP]-?\s*[0-9]{2}[A-Z]?)?)\s*[—:.\-]?\s*([\s\S]*)$/;
    const mk = function (cls, val) {
      const s = pdoc.createElement("span"); s.className = cls;
      s.textContent = (val == null ? "" : String(val));
      s.setAttribute("style", "font-size:0;line-height:0;"); return s;
    };
    pdoc.querySelectorAll("h1, h2, h3").forEach(function (h) {
      const txt = (h.textContent || "").replace(/\s+/g, " ").trim();
      if (!txt) return;
      const m = txt.match(CODE_RE);
      const hasName = !!(m && m[2] && m[2].trim());
      const sm = (h.id && secMeta[h.id]) || {};
      const secno = sm.section_no || (hasName ? m[1] : "");
      const sname = sm.section || (hasName ? m[2].trim() : txt);
      const issno = sm.issue_no || issueNo;
      const issdt = sm.issue_date || issueDate;
      const amdno = sm.amend_no || amendNo;
      const amddt = sm.amend_date || amendDate;
      const pgno = sm.page_no || "";
      if (pgno) h.setAttribute("data-pageno", pgno);
      // Insert in reverse so the final DOM order is sno, sname, issno, issdt, amdno, amddt.
      [mk("mk-amddt", amddt), mk("mk-amdno", amdno), mk("mk-issdt", issdt),
       mk("mk-issno", issno), mk("mk-sname", sname), mk("mk-sno", secno)]
        .forEach(function (sp) { h.insertBefore(sp, h.firstChild); });
    });
    bodyHtml = pdoc.body.innerHTML;
  } catch (e) { bodyHtml = html || ""; }
  // Seed the running-header strings with the document defaults so the cover / any content before
  // the first heading still shows complete control values.
  const seed = '<div style="height:0;overflow:hidden;">'
    + '<span class="mk-issno">' + esc(issueNo) + '</span><span class="mk-issdt">' + esc(issueDate) + '</span>'
    + '<span class="mk-amdno">' + esc(amendNo) + '</span><span class="mk-amddt">' + esc(amendDate) + '</span></div>';
  const sigCell = function (slot, label) {
    const s = sigs[slot] || {};
    const img = s.signature ? '<img class="sig" src="' + s.signature + '" alt="' + esc(label) + '"/>' : '';
    const nm = s.name ? '<div class="signm">' + esc(s.name) + (s.designation ? ' (' + esc(s.designation) + ')' : '') + '</div>' : '';
    return label + ':' + img + nm;
  };
  const foot =
    '<table class="ctrlfoot"><tbody>'
    + '<tr><td colspan="2">Doc. No.: <b>' + esc(docNo) + '</b></td><td colspan="3">Doc. Name: <b>' + esc(docName) + '</b></td></tr>'
    + '<tr><td colspan="2">Section No.: <b><span class="v-sno"></span></b></td><td colspan="3">Section: <b><span class="v-sname"></span></b></td></tr>'
    + '<tr><td>Issue No.: <b><span class="v-issno"></span></b></td><td>Issue Date: <span class="v-issdt"></span></td><td>Amend. No.: <b><span class="v-amdno"></span></b></td><td>Amend Date: <span class="v-amddt"></span></td><td class="pgtd">Page: <b><span class="v-pg" data-auto="1"></span></b></td></tr>'
    + '<tr><td colspan="2" class="sigtd">' + sigCell("prepared_by", "Prepared By") + '</td><td colspan="2" class="sigtd">' + sigCell("approved_by", "Approved By") + '</td><td class="sigtd">' + sigCell("issued_by", "Issued By") + '</td></tr>'
    + '</tbody></table>';
  const css =
    '@page{size:A4;margin:16mm 12mm 58mm 12mm;@bottom-center{content:element(ctrlfoot);}@top-right{content:"CONTROLLED";color:#c02121;font-family:Arial,sans-serif;font-weight:700;letter-spacing:2px;font-size:10px;}}'
    + 'body{font-family:Georgia,"Times New Roman",serif;color:#111;line-height:1.6;margin:0;overflow-wrap:break-word;}'
    + 'p,li,h1,h2,h3,h4{overflow-wrap:break-word;word-break:normal;}'
    + 'table{width:100%;border-collapse:collapse;margin:8px 0;}tr{break-inside:avoid;}'
    + 'th,td{border:1px solid #333;padding:6px 9px;font-size:13px;text-align:left;vertical-align:top;overflow-wrap:break-word;word-break:break-word;}th{background:#eee;}'
    + 'table{table-layout:auto;}td.sl-cell,th.sl-cell{width:1%;white-space:nowrap;text-align:center;overflow-wrap:normal;word-break:keep-all;}'
    // Wide Scope-of-the-Laboratory tables: fixed 7-column layout (colgroup widths), word-wrap
    // (never letter-by-letter), and a repeating column header on every continuation page.
    + 'table.scope-table{table-layout:fixed;}'
    + '.scope-table thead{display:table-header-group;}'
    + '.scope-table th,.scope-table td{font-size:11px;padding:4px 6px;vertical-align:top;word-break:normal;overflow-wrap:break-word;hyphens:none;}'
    + '.scope-table thead th{background:#eee;text-align:center;font-weight:700;line-height:1.25;}'
    + '.scope-table td.scope-sl{text-align:center;}'
    + '.scope-table td.scope-banner{background:#f0f0f0;font-weight:700;text-align:left;}'
    + '.orgchart{text-align:center;margin:12px 0;break-inside:avoid;}.orgchart svg{max-width:100%;height:auto;}'
    + 'img{max-width:100%;height:auto;}'
    + 'h1{font-size:20px;margin:18px 0 8px;break-before:page;border-bottom:2px solid #333;padding-bottom:6px;}h1:first-of-type{break-before:avoid;}'
    + 'h2{font-size:17px;margin:16px 0 6px;border-bottom:1px solid #999;padding-bottom:4px;break-after:avoid;}'
    // Front-matter section titles (Amendment Record / Contents) and the release lead line: centered,
    // no section border — look like the printed controlled copy, not a clause heading.
    + 'h2.qm-fm-title{text-align:center;border:none;padding:0;font-size:15px;margin:10px 0;}'
    + 'h2.qm-fm-lead{text-align:center;border:none;padding:0;font-size:14px;margin:8px 0;}'
    + 'h3{font-size:14.5px;margin:12px 0 4px;break-after:avoid;}'
    + 'h2.fm-title{break-before:page;border:1px solid #333;background:#eee;padding:6px 10px;}'
    + 'a.doc-ref{color:#111;text-decoration:none;font-weight:600;white-space:nowrap;}ol,ul{line-height:1.65;}'
    + '.noprint,.sheet-print{display:none;}'
    + '.ph{text-align:center;position:relative;margin-bottom:12px;border-bottom:1px solid #333;padding-bottom:8px;}'
    + '.ph .c{position:absolute;right:0;top:0;font-size:12px;}.ph .l{font-weight:700;font-size:15px;}.ph .t{font-weight:700;font-size:18px;margin-top:4px;}'
    + '.mk-sno{string-set:secno content();}.mk-sname{string-set:sname content();}'
    + '.mk-issno{string-set:issno content();}.mk-issdt{string-set:issdt content();}'
    + '.mk-amdno{string-set:amdno content();}.mk-amddt{string-set:amddt content();}'
    + '.ctrlfoot{position:running(ctrlfoot);width:100%;border-collapse:collapse;font-family:Arial,sans-serif;}'
    + '.ctrlfoot td{border:1px solid #333;padding:5px 9px;font-size:12px;font-weight:400;line-height:1.35;overflow-wrap:break-word;word-break:break-word;}'
    + '.ctrlfoot td.sigtd{text-align:center;vertical-align:bottom;padding:6px 8px 5px;width:33%;}'
    + '.ctrlfoot td.pgtd{white-space:nowrap;text-align:center;}'
    + '.ctrlfoot img.sig{height:36px;max-width:100%;object-fit:contain;display:block;margin:3px auto 2px;}'
    + '.ctrlfoot .signm{font-size:10.5px;color:#333;line-height:1.25;}'
    + '.v-sno::after{content:string(secno);}.v-sname::after{content:string(sname);}'
    + '.v-issno::after{content:string(issno);}.v-issdt::after{content:string(issdt);}'
    + '.v-amdno::after{content:string(amdno);}.v-amddt::after{content:string(amddt);}'
    + '.v-pg[data-auto]::after{content:counter(page);}'
    // On screen, show each A4 page as a distinct sheet stacked vertically so the reader sees the
    // control box at the foot of every individual page. Ignored by the actual print engine.
    + (preview ? 'html,body{background:#e9ecef;}.pagedjs_pages{padding:8px 0;}.pagedjs_page{background:#fff;margin:0 auto 14px;box-shadow:0 1px 6px rgba(0,0,0,.22);position:relative;}'
        + '.pg-edit-btn{position:absolute;right:10px;bottom:10px;z-index:50;font:600 11px Arial,sans-serif;padding:4px 11px;background:#2563eb;color:#fff;border:none;border-radius:6px;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.3);}'
        + '.pg-edit-btn:hover{background:#1d4ed8;}'
        + '.mk-hl{background:#fde68a !important;transition:background .3s;}' : '');
  // Plain sequential page numbering: every page shows the natural running page number (1, 2, 3, 4,
  // 5 …) via the CSS `counter(page)` default. No section-based renumbering, no decimal (4.1 / 1.4)
  // format, no restart. A page number is overridden ONLY when the section active at the top of a page
  // carries an explicit manual Page No. (data-pageno) — otherwise the counter is left untouched.
  const fillPages = 'function(){try{var pgs=document.querySelectorAll(".pagedjs_page");var carry="";'
    + 'pgs.forEach(function(pg){var hs=pg.querySelectorAll("[data-pageno]");'
    + 'if(hs.length){var lv=(hs[hs.length-1].getAttribute("data-pageno")||"").trim();if(lv)carry=lv;}'
    + 'if(carry){var c=pg.querySelector(".pagedjs_margin-bottom-center .v-pg");if(c){c.removeAttribute("data-auto");c.textContent=carry;}}});'
    + '}catch(e){}}';
  // Hide the running footer (and top-right CONTROLLED) on the COVER page only. Done in JS after layout
  // because paged.js 0.4.3 does not honour `@page:first { @bottom-center { content:none } }`.
  const coverFn = coverNoFooter
    ? 'function(){try{var p=document.querySelector(".pagedjs_page");if(!p)return;var f=p.querySelector(".pagedjs_margin-bottom-center");if(f)f.style.visibility="hidden";var t=p.querySelector(".pagedjs_margin-top-right");if(t)t.style.visibility="hidden";}catch(e){}}'
    : 'function(){}';
  // Preview only: scale each A4 page down to fit the container width so the WHOLE page (content +
  // footer) is visible with NO horizontal scrollbar — the real print keeps true A4. zoom is applied
  // to the body (reflows, so no leftover horizontal overflow); the reported height is the original
  // height × zoom so the outer iframe grows to exactly the scaled document and scrolls vertically.
  const fitFn = 'function(){try{var pg=document.querySelector(".pagedjs_page");if(!pg)return;'
    + 'document.body.style.zoom="";'
    + 'var pw=pg.getBoundingClientRect().width;var oh=document.body.scrollHeight;'
    + 'var av=(document.documentElement.clientWidth||window.innerWidth)-2;'
    + 'var z=(pw>av&&pw>0)?(av/pw):1;document.body.style.zoom=z;'
    + 'parent.postMessage({__pagedFrame:1,__pagedHeight:Math.ceil(oh*z)+16},"*");'
    + '}catch(e){}}';
  // Preview only: put a small "✎ Edit" button on each page that opens the per-page document-control
  // editor in the parent. Each button carries the id of the section whose values that page's footer
  // shows (the last heading on the page, or the one carried forward) so editing affects ONLY that
  // section — never every page. Buttons are editor-only (never built for the Print/PDF window).
  const injectEdit = 'function(){try{var pgs=document.querySelectorAll(".pagedjs_page");var last="";'
    + 'pgs.forEach(function(pg){'
    + 'var hs=pg.querySelectorAll("h1[id],h2[id],h3[id]");if(hs.length){last=hs[hs.length-1].id;}var sid=last;'
    + 'if(pg.querySelector(".pg-edit-btn"))return;'
    + 'var pc=pg.querySelector(".pagedjs_margin-bottom-center .v-pg");var lbl=pc?pc.textContent:"";'
    + 'var b=document.createElement("button");b.className="pg-edit-btn";b.type="button";b.textContent="\\u270E Edit";'
    + 'b.onclick=function(e){e.preventDefault();e.stopPropagation();parent.postMessage({__pagedFrame:1,__pagedEdit:1,secId:sid,pageLabel:lbl},"*");};'
    + 'pg.appendChild(b);});}catch(e){}}';
  // paged.js 0.4.3 drops BOTH the <colgroup> AND the <thead> from the continuation fragments of a
  // table that spans several pages. Without the colgroup the table-layout:fixed widths are lost, so
  // the narrow Sl column collapses and its number overlaps the next column (looks like a numbering
  // jump). Carry each scope-table's own colgroup + header forward onto its continuation fragments so
  // every page shows the 7-column header AND keeps the exact column widths (matches the ISO master).
  const repeatHdr = 'function(){try{var lastThead="",lastCg="";'
    + 'document.querySelectorAll(".pagedjs_page table.scope-table").forEach(function(t){'
    + 'var th=t.querySelector("thead");var cg=t.querySelector("colgroup");'
    + 'if(th)lastThead=th.outerHTML;if(cg)lastCg=cg.outerHTML;'
    + 'if(lastCg&&!cg){t.insertAdjacentHTML("afterbegin",lastCg);}'
    + 'if(lastThead&&!th){var c=t.querySelector("colgroup");if(c){c.insertAdjacentHTML("afterend",lastThead);}else{t.insertAdjacentHTML("afterbegin",lastThead);}}'
    + '});}catch(e){}}';
  // After paged.js lays out the pages: repeat scope headers, fill page numbers, then either fit-to-width
  // + add edit buttons + report height (inline preview) or trigger the print dialog (Print/PDF window).
  const afterBody = preview
    ? 'function(){window.__repeatHdr();window.__fillPages();window.__cover();window.__injectEdit();window.__fit();setTimeout(function(){window.__repeatHdr();window.__cover();window.__injectEdit();window.__fit();},400);setTimeout(function(){window.__repeatHdr();window.__cover();window.__injectEdit();window.__fit();},1500);setTimeout(window.__fit,3500);}'
    : 'function(){window.__repeatHdr();window.__fillPages();window.__cover();setTimeout(function(){try{window.focus();window.print();}catch(e){}},400);}';
  const tailScript = preview ? '' :
    '<script>setTimeout(function(){if(!document.querySelector(".pagedjs_page")){try{window.print();}catch(e){}}},8000);<\/script>';
  return '<html><head><meta charset="utf-8"><title>' + esc(title) + '</title><style>' + css + '</style>'
    + '<script>window.__fillPages=' + fillPages + ';window.__repeatHdr=' + repeatHdr + ';window.__cover=' + coverFn + ';'
    + (preview ? 'window.__fit=' + fitFn + ';window.__injectEdit=' + injectEdit + ';window.addEventListener("resize",function(){clearTimeout(window.__rt);window.__rt=setTimeout(window.__fit,200);});' : '')
    + 'window.PagedConfig={auto:true,after:' + afterBody + '};<\/script>'
    + '<script src="https://unpkg.com/pagedjs@0.4.3/dist/paged.polyfill.js"><\/script>'
    + '</head><body>'
    + '<div class="ph"><div class="c">' + esc(code || "") + '</div><div class="l">Sanber Labs LLP (Test Lab)</div><div class="t">' + esc(title) + '</div></div>'
    + seed + foot + bodyHtml + tailScript
    + '</body></html>';
}

// ---------------------------------------------------------------------------
// EXPLICIT fixed-page renderer.
// Unlike buildPagedHtml (which hands a single HTML blob to paged.js and lets it
// decide where each A4 page ends), this renders a caller-supplied ARRAY of pages
// — one <section class="qm-sheet"> per stored page — so the ORIGINAL hard-copy
// page boundaries are honoured exactly and never recomputed by the browser.
// Each page carries its OWN footer values (section no/name, amend no/date, the
// printed Page number) taken verbatim from the stored page record, so source
// page-number typos and blank pages are reproduced literally and remain editable
// data (never an image). Signatures (Prepared/Approved/Issued By) come from the
// document-level footerMeta, matching the controlled-copy footer.
function buildFixedPagesHtml(title, code, pages, footerMeta, opts) {
  opts = opts || {};
  const preview = !!opts.preview;
  const esc = function (s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
  const fm = footerMeta || {};
  const docNo = fm.docNo || String(code || "").replace(/SAN-?Lab/i, "SANLAB");
  const docName = (fm.docName || String(title || "")).toUpperCase();
  const issueNo = fm.issueNo || "01", issueDate = fm.issueDate || "05/10/2024";
  const sigs = fm.signatures || {};
  const sigCell = function (slot, label) {
    const s = sigs[slot] || {};
    const img = s.signature ? '<img class="sig" src="' + s.signature + '" alt="' + esc(label) + '"/>' : '';
    const nm = s.name ? '<div class="signm">' + esc(s.name) + (s.designation ? ' (' + esc(s.designation) + ')' : '') + '</div>' : '';
    return label + ':' + img + nm;
  };
  const footFor = function (pg) {
    return '<table class="ctrlfoot"><tbody>'
      + '<tr><td colspan="2">Doc. No.: <b>' + esc(docNo) + '</b></td><td colspan="3">Doc. Name: <b>' + esc(docName) + '</b></td></tr>'
      + '<tr><td colspan="2">Section No.: <b>' + esc(pg.section_no) + '</b></td><td colspan="3">Section: <b>' + esc(pg.section) + '</b></td></tr>'
      + '<tr><td>Issue No.: <b>' + esc(issueNo) + '</b></td><td>Issue Date: ' + esc(issueDate) + '</td><td>Amend. No.: <b>' + esc(pg.amend_no) + '</b></td><td>Amend Date: ' + esc(pg.amend_date) + '</td><td class="pgtd">Page: <b>' + esc(pg.page) + '</b></td></tr>'
      + '<tr><td colspan="2" class="sigtd">' + sigCell("prepared_by", "Prepared By") + '</td><td colspan="2" class="sigtd">' + sigCell("approved_by", "Approved By") + '</td><td class="sigtd">' + sigCell("issued_by", "Issued By") + '</td></tr>'
      + '</tbody></table>';
  };
  // --- Scope-of-the-laboratory (ANX.F) table ---
  // The source stored this long table as several FIXED row-chunks, each a full
  // <table class="scope-table"> with its OWN <thead> — so the 7-column header repeated on every
  // page and each page held only ~10 rows (big blank gaps, rows pushed to the next page). Combine
  // every chunk into ONE table (header + lab banner shown ONCE) and let __flowScope split the rows
  // across as many A4 sheets as REALLY fit. The column <colgroup> keeps the columns aligned on the
  // headerless continuation sheets. Non-scope pages are rendered exactly as before.
  let scopeStart = -1, scopeEnd = -1, scopeTheadHtml = "", scopeColHtml = "", scopeRowsHtml = "";
  (pages || []).forEach(function (p, i) { if (/scope-table/.test(p.html || "")) { if (scopeStart < 0) scopeStart = i; scopeEnd = i; } });
  if (scopeStart >= 0) {
    try {
      const dp = new DOMParser();
      for (let si = scopeStart; si <= scopeEnd; si++) {
        const pdoc = dp.parseFromString(pages[si].html || "", "text/html");
        const t = pdoc.querySelector("table.scope-table"); if (!t) continue;
        if (si === scopeStart) {
          const th = t.querySelector("thead"); if (th) scopeTheadHtml = th.outerHTML;
          const cg = t.querySelector("colgroup"); if (cg) scopeColHtml = cg.outerHTML;
        }
        const tb = t.querySelector("tbody");
        scopeRowsHtml += tb ? tb.innerHTML : Array.from(t.querySelectorAll("tr")).filter(function (r) { return !r.closest("thead"); }).map(function (r) { return r.outerHTML; }).join("");
      }
    } catch (e) { scopeStart = -1; }
  }
  // Hard-code the 7 scope column widths in the renderer so the columns NEVER collapse — even if a
  // database somewhere is missing/has a bad <colgroup> (the Live import-corruption symptom). With
  // table-layout:fixed this fully determines the layout from code, independent of the stored data.
  scopeColHtml = '<colgroup><col style="width:4%"/><col style="width:14%"/><col style="width:20%"/><col style="width:21%"/><col style="width:15%"/><col style="width:18%"/><col style="width:8%"/></colgroup>';
  const scopeStartPage = scopeStart >= 0 ? (parseInt(pages[scopeStart].page, 10) || 1) : 1;
  const scopeFootTpl = scopeStart >= 0 ? footFor(Object.assign({}, pages[scopeStart], { page: "___PG___" })) : "";
  const scopeBanner = scopeStart >= 0 ? '<div class="ph"><div class="c">' + esc(code || "") + '</div><div class="l">Sanber Labs LLP (Test Lab)</div><div class="t">' + esc(title) + '</div></div>' : "";
  const sheets = (pages || []).map(function (pg, i) {
    if (scopeStart >= 0 && i > scopeStart && i <= scopeEnd) return "";   // merged into the flow below
    if (scopeStart >= 0 && i === scopeStart) {
      return '<div id="scope-ph" style="display:none"><table class="scope-table">' + scopeColHtml + scopeTheadHtml + '<tbody>' + scopeRowsHtml + '</tbody></table></div>';
    }
    const html = pg.html || "";
    const prevHtml = (pages[i - 1] || {}).html || "";
    // A page that CONTINUES a multi-page entries table (the ANX.F "Scope of the Laboratory"
    // table spans several pages): the big lab header banner + CONTROLLED stamp must NOT repeat
    // in the middle of the entries. Show them only on the FIRST page where the table begins
    // (i.e. the previous page did not already contain the same scope table). Footer + the
    // table's own column header / discipline rows are untouched.
    const isTableCont = i > 0 && /scope-table/.test(html) && /scope-table/.test(prevHtml);
    const showBanner = !pg.no_footer && !isTableCont;
    // Section-divider title page ("Section 7 — Process Requirements …"): centre + balance it.
    // Strip tags + &nbsp; first so the match is robust to the stored markup.
    const headText = html.replace(/<[^>]+>/g, " ").replace(/&nbsp;|&#160;/g, " ").replace(/\s+/g, " ").trim();
    const isDivider = /^Section\s+\d+\s*[—–\-]/i.test(headText);
    const controlled = showBanner ? '<div class="qm-stamp">CONTROLLED</div>' : '';
    const foot = pg.no_footer ? '' : '<div class="qm-foot">' + footFor(pg) + '</div>';
    const amend = (pg.amendment && String(pg.amendment).trim())
      ? '<div class="qm-amend"><span class="qm-amend-tag">Amendment</span>' + pg.amendment + '</div>' : '';
    const editBtn = preview ? '<button class="pg-edit-btn" type="button" data-pi="' + i + '">✎ Edit page</button>' : '';
    // The stored content wraps EVERY word in &nbsp; (a non-breaking space, from the original
    // import). That stops the browser breaking lines at spaces, so justified text is forced to
    // break mid-word ("fi nancial"). Render with ordinary spaces so text wraps at word boundaries.
    // Display-only — the stored data is untouched.
    const body = (html && String(html).trim()) ? html.replace(/&nbsp;|&#160;| /g, ' ') : (pg.no_footer ? '' : '<div class="qm-blank">(This page is intentionally left blank in the source document.)</div>');
    // The lab header banner repeats on every controlled page; the cover page (no_footer) carries its
    // own title block in its content, so it gets no banner, no CONTROLLED stamp and no footer.
    const banner = showBanner ? '<div class="ph"><div class="c">' + esc(code || "") + '</div><div class="l">Sanber Labs LLP (Test Lab)</div><div class="t">' + esc(title) + '</div></div>' : '';
    return '<section class="qm-sheet' + (pg.no_footer ? ' qm-cover' : '') + (isDivider ? ' qm-divider' : '') + '" data-pi="' + i + '">'
      + controlled + banner
      + '<div class="qm-body">' + amend + body + '</div>'
      + foot + editBtn
      + '</section>';
  }).join("");
  const css =
    'body{font-family:Georgia,"Times New Roman",serif;color:#111;line-height:1.6;margin:0;overflow-wrap:break-word;background:' + (preview ? '#e9ecef' : '#fff') + ';}'
    + 'p,li,h1,h2,h3,h4{overflow-wrap:break-word;word-break:normal;}'
    + 'table{width:100%;border-collapse:collapse;margin:8px 0;}tr{break-inside:avoid;}'
    + 'th,td{border:1px solid #333;padding:6px 9px;font-size:13px;text-align:left;vertical-align:top;overflow-wrap:break-word;word-break:break-word;}th{background:#eee;}'
    + 'table{table-layout:auto;}td.sl-cell,th.sl-cell{width:1%;white-space:nowrap;text-align:center;overflow-wrap:normal;word-break:keep-all;}'
    + 'table.scope-table{table-layout:fixed;}.scope-table thead{display:table-header-group;}'
    + '.scope-table th,.scope-table td{font-size:11px;padding:4px 6px;vertical-align:top;word-break:normal;overflow-wrap:break-word;hyphens:none;}'
    + '.scope-table thead th{background:#eee;text-align:center;font-weight:700;line-height:1.25;}'
    + '.scope-table td.scope-sl{text-align:center;}.scope-table td.scope-banner{background:#f0f0f0;font-weight:700;text-align:left;}'
    + '.orgchart{text-align:center;margin:12px 0;break-inside:avoid;}.orgchart svg{max-width:100%;height:auto;}img{max-width:100%;height:auto;}'
    + 'h1{font-size:20px;margin:4px 0 8px;border-bottom:2px solid #333;padding-bottom:6px;}'
    + 'h2{font-size:17px;margin:16px 0 6px;border-bottom:1px solid #999;padding-bottom:4px;}'
    + 'h2.qm-fm-title{text-align:center;border:none;padding:0;font-size:15px;margin:10px 0;}'
    + 'h2.qm-fm-lead{text-align:center;border:none;padding:0;font-size:14px;margin:8px 0;}'
    + 'h3{font-size:14.5px;margin:12px 0 4px;}a.doc-ref{color:#111;text-decoration:none;font-weight:600;white-space:nowrap;}ol,ul{line-height:1.65;}'
    + '.noprint{display:none;}'
    // The A4 sheet: fixed width, min full-page height, print margins as padding; footer pinned to
    // the foot via flex so it sits at the bottom of every page (growing taller only if content is tall).
    + '.qm-sheet{position:relative;box-sizing:border-box;width:210mm;min-height:297mm;background:#fff;'
    + 'padding:14mm 12mm 12mm;display:flex;flex-direction:column;' + (preview ? 'margin:0 auto 16px;box-shadow:0 1px 6px rgba(0,0,0,.22);' : 'margin:0;') + '}'
    + '.qm-body{flex:1 1 auto;}'
    + '.qm-foot{margin-top:14px;flex:0 0 auto;}'
    + '.qm-stamp{position:absolute;right:12mm;top:5mm;color:#c02121;font-family:Arial,sans-serif;font-weight:700;letter-spacing:2px;font-size:10px;}'
    + '.qm-blank{color:#888;font-style:italic;text-align:center;margin-top:40mm;}'
    // Honour the editor's paragraph alignment (Quill ql-align-*) inside the paged sheets — this is
    // what centres the cover/title page text (it was rendering left-aligned without these rules).
    + '.ql-align-center{text-align:center;}.ql-align-right{text-align:right;}.ql-align-justify{text-align:justify;}'
    // Cover / title page (page 1): centre everything, give the title block a proper size and roomy
    // line spacing so it reads like a document cover. Content is untouched — presentation only.
    + '.qm-cover .qm-body{text-align:center;font-size:15px;line-height:1.95;padding:8mm 12mm 0;}'
    + '.qm-cover .qm-body p{margin:0;}'
    + '.qm-cover .qm-body>p:first-child,.qm-cover .qm-body>p:first-child strong{font-size:30px;letter-spacing:1px;line-height:1.3;}'
    + '.qm-cover .qm-body>p:nth-child(2),.qm-cover .qm-body>p:nth-child(2) strong{font-size:18px;}'
    + '.qm-cover .qm-body>p:nth-child(3),.qm-cover .qm-body>p:nth-child(3) strong{font-size:16px;}'
    // Section-divider title page: centre the existing title/clause-range/first-clause text and
    // balance it vertically on the sheet. Typography only — no boxes, colours or extra content.
    + '.qm-divider .qm-body{display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;gap:14px;padding:0 10mm;}'
    + '.qm-divider .qm-body h1{border:none;padding:0;margin:0;font-size:23px;letter-spacing:.5px;}'
    + '.qm-divider .qm-body h2{border:none;padding:0;margin:0;font-size:18px;}'
    + '.qm-divider .qm-body h3{margin:0;}'
    + '.qm-divider .qm-body p{margin:0;font-size:15px;}'
    + '.qm-amend{border:1px dashed #c08a21;background:#fffaf0;padding:8px 10px;margin:0 0 10px;font-size:13px;}'
    + '.qm-amend-tag{display:inline-block;font:700 10px Arial,sans-serif;color:#9a6a10;letter-spacing:1px;margin-right:8px;text-transform:uppercase;}'
    + '.ph{text-align:center;position:relative;margin-bottom:12px;border-bottom:1px solid #333;padding-bottom:8px;}'
    + '.ph .c{position:absolute;right:0;top:0;font-size:12px;}.ph .l{font-weight:700;font-size:15px;}.ph .t{font-weight:700;font-size:18px;margin-top:4px;}'
    + '.ctrlfoot{width:100%;border-collapse:collapse;font-family:Arial,sans-serif;}'
    + '.ctrlfoot td{border:1px solid #333;padding:5px 9px;font-size:12px;font-weight:400;line-height:1.35;overflow-wrap:break-word;word-break:break-word;}'
    + '.ctrlfoot td.sigtd{text-align:center;vertical-align:bottom;padding:6px 8px 5px;width:33%;}'
    + '.ctrlfoot td.pgtd{white-space:nowrap;text-align:center;}'
    + '.ctrlfoot img.sig{height:36px;max-width:100%;object-fit:contain;display:block;margin:3px auto 2px;}'
    + '.ctrlfoot .signm{font-size:10.5px;color:#333;line-height:1.25;}'
    + (preview
        ? '.pg-edit-btn{position:absolute;right:10px;bottom:10px;z-index:50;font:600 11px Arial,sans-serif;padding:4px 11px;background:#2563eb;color:#fff;border:none;border-radius:6px;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.3);}'
        + '.pg-edit-btn:hover{background:#1d4ed8;}.mk-hl{outline:3px solid #fde68a;outline-offset:-3px;transition:outline .3s;}'
        + 'a.qm-xref{color:#1b4e8a;text-decoration:none;cursor:pointer;border-bottom:1px dotted #1b4e8a;white-space:nowrap;}a.qm-xref:hover{text-decoration:underline;background:#eef4ff;}'
        : '@page{size:A4;margin:0;}.qm-sheet{break-after:page;}.qm-sheet:last-child{break-after:auto;}a.qm-xref{color:inherit;text-decoration:none;border:none;white-space:nowrap;}');
  // Preview: scale each sheet to fit the iframe width and report the total height so the outer page
  // scrolls through every sheet; wire the per-page Edit buttons to postMessage the parent editor.
  const fitFn = 'function(){try{var pg=document.querySelector(".qm-sheet");if(!pg)return;document.body.style.zoom="";'
    + 'var pw=pg.getBoundingClientRect().width;var oh=document.body.scrollHeight;'
    + 'var av=(document.documentElement.clientWidth||window.innerWidth)-2;var z=(pw>av&&pw>0)?(av/pw):1;'
    + 'document.body.style.zoom=z;parent.postMessage({__pagedFrame:1,__pagedHeight:Math.ceil(oh*z)+16},"*");}catch(e){}}';
  const wireFn = 'function(){try{document.querySelectorAll(".pg-edit-btn").forEach(function(b){b.onclick=function(e){e.preventDefault();e.stopPropagation();parent.postMessage({__pagedFrame:1,__pagedEdit:1,pageIndex:parseInt(b.getAttribute("data-pi"),10)},"*");};});'
    + 'document.addEventListener("click",function(e){var a=e.target.closest&&e.target.closest("a.qm-xref");if(a){e.preventDefault();parent.postMessage({__pagedFrame:1,__pagedNav:1,pageIndex:parseInt(a.getAttribute("data-goto"),10)},"*");}});}catch(e){}}';
  // Natural-flow paginator for the combined scope table: fill each A4 sheet with as many rows as
  // genuinely fit (the sheet grows past 297mm only when a row overflows → that row starts the next
  // sheet), header + banner on the FIRST sheet only, a footer with the running page number on every
  // sheet. Runs in BOTH the preview iframe and the Print/PDF window so they paginate identically.
  const scopeMetaJs = scopeStart >= 0
    ? 'window.__scopeMeta=' + JSON.stringify({ startPage: scopeStartPage, foot: scopeFootTpl, banner: scopeBanner }) + ';'
    : '';
  const flowFn = 'function(){try{var ph=document.getElementById("scope-ph");if(!ph||!window.__scopeMeta)return;document.body.style.zoom="";'
    + 'var m=window.__scopeMeta;var tbl=ph.querySelector("table.scope-table");var thead=tbl.querySelector("thead");var colg=tbl.querySelector("colgroup");'
    + 'var rows=[].slice.call(tbl.querySelectorAll("tbody>tr"));var A4=297*96/25.4;var parent=ph.parentNode;var ri=0,pg=m.startPage,first=true;'
    + 'while(ri<rows.length){var sheet=document.createElement("section");sheet.className="qm-sheet";'
    + 'sheet.innerHTML="<div class=\\"qm-stamp\\">CONTROLLED</div>"+(first?m.banner:"");'
    + 'var body=document.createElement("div");body.className="qm-body";var t=document.createElement("table");t.className="scope-table";'
    + 'if(colg)t.appendChild(colg.cloneNode(true));if(first&&thead)t.appendChild(thead.cloneNode(true));'
    + 'var tb=document.createElement("tbody");t.appendChild(tb);body.appendChild(t);sheet.appendChild(body);'
    + 'var foot=document.createElement("div");foot.className="qm-foot";foot.innerHTML=m.foot.replace("___PG___",String(pg));sheet.appendChild(foot);'
    + 'parent.insertBefore(sheet,ph);'
    + 'while(ri<rows.length){var r=rows[ri].cloneNode(true);tb.appendChild(r);'
    + 'if(sheet.getBoundingClientRect().height>A4+2){if(tb.children.length>1){tb.removeChild(r);break;}}ri++;}'
    + 'first=false;pg++;}parent.removeChild(ph);}catch(e){}}';
  const head = '<html><head><meta charset="utf-8"><title>' + esc(title) + '</title><style>' + css + '</style>';
  if (preview) {
    return head + '<script>' + scopeMetaJs + 'window.__flowScope=' + flowFn + ';window.__fit=' + fitFn + ';window.__wire=' + wireFn + ';'
      + 'window.addEventListener("resize",function(){clearTimeout(window.__rt);window.__rt=setTimeout(window.__fit,200);});'
      + 'window.addEventListener("load",function(){window.__flowScope();window.__wire();window.__fit();setTimeout(window.__fit,300);setTimeout(window.__fit,1200);});<\/script>'
      + '</head><body>' + sheets + '</body></html>';
  }
  return head + '<script>' + scopeMetaJs + 'window.__flowScope=' + flowFn + ';'
    + 'window.addEventListener("load",function(){window.__flowScope();setTimeout(function(){try{window.focus();window.print();}catch(e){}},300);});<\/script>'
    + '</head><body>' + sheets + '</body></html>';
}

// Open the Print/PDF window for the explicit fixed-page model.
function printPages(title, code, pages, footerMeta) {
  const w = window.open("", "_blank", "width=960,height=900");
  w.document.write(buildFixedPagesHtml(title, code, pages, footerMeta, { preview: false }));
  w.document.close();
  w.focus();
}

// On-screen page-by-page view driven by the EXPLICIT stored pages (no paged.js reflow). Mirrors
// PagedDoc's iframe/auto-height/edit-postMessage contract so the Rulebook view can swap renderers.
function FixedPagesDoc({ title, code, pages, footerMeta, minHeight, frameId, onEdit, onNav }) {
  const srcDoc = React.useMemo(
    () => buildFixedPagesHtml(title, code, pages, footerMeta, { preview: true }),
    [title, code, pages, footerMeta]);
  const [h, setH] = useState(minHeight || 1400);
  const onEditRef = React.useRef(onEdit); onEditRef.current = onEdit;
  const onNavRef = React.useRef(onNav); onNavRef.current = onNav;
  useEffect(() => {
    const onMsg = (e) => {
      const d = e && e.data;
      if (!d || !d.__pagedFrame) return;
      if (d.__pagedEdit) { if (onEditRef.current) onEditRef.current(d.pageIndex); return; }
      if (d.__pagedNav) { if (onNavRef.current) onNavRef.current(d.pageIndex); return; }
      if (typeof d.__pagedHeight === "number") setH(Math.max(minHeight || 600, d.__pagedHeight + 24));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [minHeight]);
  return <iframe id={frameId} title={"Pages — " + (title || "document")} srcDoc={srcDoc}
    style={{ width: "100%", height: h, border: "none", background: "#e9ecef", display: "block" }} />;
}

function printContent(title, code, html, footerMeta) {
  const w = window.open("", "_blank", "width=960,height=900");
  w.document.write(buildPagedHtml(title, code, html, footerMeta, { preview: false }));
  w.document.close();
  w.focus();
}

// On-screen page-by-page view: renders the SAME paged.js document as Print/PDF inside an isolated
// iframe, so the control box shows at the bottom of every page with the correct page number. The
// iframe auto-grows to its content height (reported via postMessage) so the outer page scrolls
// naturally through all the manual's pages.
function PagedDoc({ title, code, html, footerMeta, minHeight, frameId, onEdit }) {
  const srcDoc = React.useMemo(
    () => buildPagedHtml(title, code, html, footerMeta, { preview: true }),
    [title, code, html, footerMeta]);
  const [h, setH] = useState(minHeight || 1400);
  const onEditRef = React.useRef(onEdit); onEditRef.current = onEdit;
  useEffect(() => {
    const onMsg = (e) => {
      const d = e && e.data;
      if (!d || !d.__pagedFrame) return;
      if (d.__pagedEdit) { if (onEditRef.current) onEditRef.current(d.secId, d.pageLabel); return; }
      if (typeof d.__pagedHeight === "number") setH(Math.max(minHeight || 600, d.__pagedHeight + 24));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [minHeight]);
  return <iframe id={frameId} title={"Pages — " + (title || "document")} srcDoc={srcDoc}
    style={{ width: "100%", height: h, border: "none", background: "#e9ecef", display: "block" }} />;
}

function FormatHeader({ code, title }) {
  const { logo } = useAuth();
  return <div className="fmt-head">
    <div className="fmt-code">{code}</div>
    {logo && <img src={logo} alt="Sanber Labs" style={{ height: 54, marginBottom: 4 }} />}
    <div className="fmt-lab">Sanber Labs LLP <span>(Test Lab)</span></div>
    <div className="fmt-title">{title}</div>
  </div>;
}

function RecordSection({ title, coll, fields, columns, masters }) {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const load = () => api.c4List(coll).then(setItems);
  useEffect(() => { load(); }, [coll]);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const fileB64 = (file, k) => { const r = new FileReader(); r.onload = () => setF(k, r.result); r.readAsDataURL(file); };
  const save = async () => { if (form.id) await api.c4Edit(coll, form.id, form); else await api.c4Add(coll, form); setForm(null); load(); };
  const del = async (id) => { if (window.confirm("Delete this record?")) { await api.c4Del(coll, id); load(); } };
  const openNew = () => { const init = {}; fields.forEach(f => { if (f.default) init[f.key] = f.default; }); setForm(init); };
  const eye = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>;
  return <div className="card">
    <div className="row" style={{ justifyContent: "space-between" }}>
      <b>{title}</b>
      {can("clause_documents", "add") && !form && <button className="primary" onClick={openNew}>Add</button>}
    </div>
    {form ? <div style={{ marginTop: 10 }}>
      {fields.map(f => <div key={f.key}>
        <label>{f.label}</label>
        {f.type === "select"
          ? <select value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} style={{ maxWidth: 360 }}>
              <option value="">—</option>{(f.options || (masters[f.master] || [])).map(o => <option key={o}>{o}</option>)}</select>
          : f.type === "textarea"
          ? <textarea value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} rows={4} />
          : f.type === "signature"
          ? <div><input type="file" accept="image/*" onChange={e => fileB64(e.target.files[0], f.key)} />{form[f.key] && <img src={form[f.key]} alt="sig" style={{ height: 40, marginTop: 6 }} />}</div>
          : <input type={f.type === "date" ? "date" : "text"} value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} style={{ maxWidth: 360 }} />}
      </div>)}
      <div className="row" style={{ marginTop: 10 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setForm(null)}>Cancel</button></div>
    </div>
    : <table style={{ marginTop: 10 }}><thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}<th></th></tr></thead><tbody>
        {items.map(it => <tr key={it.id}>{columns.map(c => <td key={c.key}>{c.key === "signature_image" ? (it[c.key] ? <img src={it[c.key]} alt="sig" style={{ height: 28 }} /> : "—") : it[c.key]}</td>)}
          <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
            <button className="view-btn" title="View record" aria-label="View record" onClick={() => setView(it)}>{eye}</button>
            {can("clause_documents", "edit") && <button onClick={() => setForm(it)} style={{ marginLeft: 8 }}>Edit</button>}
            {can("clause_documents", "delete") && <button onClick={() => del(it.id)} style={{ marginLeft: 8 }}>Delete</button>}
          </td></tr>)}
      </tbody></table>}
    {view && <div className="modal-overlay" onClick={() => setView(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}><b>{title}</b><button onClick={() => setView(null)}>Close</button></div>
        <table><tbody>
          {fields.map(f => <tr key={f.key}>
            <td style={{ color: "var(--text-2)", width: "38%" }}>{f.label}</td>
            <td>{f.type === "signature" ? (view[f.key] ? <img src={view[f.key]} alt="signature" style={{ height: 44 }} /> : "—") : (view[f.key] || "—")}</td>
          </tr>)}
        </tbody></table>
      </div>
    </div>}
  </div>;
}

function FormatTable({ code, title, coll, columns, footer, filter, headerGroups, userFillable, approvalAfter, approvalLabel }) {
  const apLabel = approvalLabel || "Signed by";
  const { can } = useAuth();
  // userFillable registers (daily-entry logs) can be edited/appended by any signed-in user,
  // regardless of the clause_documents role permissions.
  const canEdit = userFillable || can("clause_documents", "edit");
  const canAdd = userFillable || can("clause_documents", "add");
  const canDelete = userFillable || can("clause_documents", "delete");
  // Attaching documents to a record needs only "add" (or edit) — a lighter bar than full edit,
  // so users who can create records can also upload supporting files.
  const canAttach = canAdd || canEdit;
  const importRef = React.useRef(null);
  // Bulk import a CSV into this register. Columns are matched by HEADER NAME (not by position),
  // so an extra/missing leading "Sl No." column can never shift the data one place over. If no
  // recognisable header is present we fall back to strict positional mapping. Date columns are
  // normalised; the source serial ("Sl No.") is captured into sl_no so the register shows the
  // sheet's real numbering, and file order is stamped into seq.
  const norm = (s) => String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const tokens = (s) => new Set(norm(s).split(" ").filter(Boolean));
  const isSerialHeader = (t) => t.has("serial") || ((t.has("sl") || t.has("sr") || t.has("s")) && t.has("no") && !t.has("unique"));
  const importCSV = async (file) => {
    if (!file) return;
    try {
      const parsed = parseCSV(await file.text()).filter(r => r.some(c => String(c || "").trim() !== ""));
      if (!parsed.length) { window.alert("No rows found in the file."); return; }
      const first = parsed[0];
      const looksHeader = first.some(c => /requisition|item|sample|unique|testing|receipt|date|mtc|heat|internal|external|remark|no\.|sl\b|serial/i.test(String(c || "")));

      let dataRows, colIdx, serialIdx = -1;
      if (looksHeader) {
        // Match each register column to the CSV column whose header shares the most words.
        const htok = first.map(tokens);
        const used = new Set();
        htok.forEach((t, j) => { if (serialIdx < 0 && isSerialHeader(t)) { serialIdx = j; used.add(j); } });
        colIdx = columns.map(c => {
          const ct = tokens(c.label);
          let best = -1, bestScore = 0;
          htok.forEach((t, j) => {
            if (used.has(j)) return;
            let score = 0; ct.forEach(tok => { if (t.has(tok)) score++; });
            if (score > bestScore) { bestScore = score; best = j; }
          });
          if (best >= 0 && bestScore > 0) { used.add(best); return best; }
          return -1;
        });
        const unmatched = columns.filter((c, i) => colIdx[i] < 0).map(c => (c.label || c.key));
        if (unmatched.length) {
          window.alert("Could not match these columns from the file header:\n\n" + unmatched.join("\n") +
            "\n\nRename the file's header cells to match, then try again. Nothing was imported.");
          return;
        }
        dataRows = parsed.slice(1);
      } else {
        // No header: strict positional. A leading all-numeric serial column is skipped.
        const off = (parsed.length && /^\d+$/.test(String(parsed[0][0] || "").trim())) ? 1 : 0;
        serialIdx = off === 1 ? 0 : -1;
        colIdx = columns.map((c, i) => off + i);
        dataRows = parsed;
      }

      const rows = dataRows.map(r => {
        const obj = {};
        columns.forEach((c, i) => { let v = String(r[colIdx[i]] == null ? "" : r[colIdx[i]]).trim(); if (c.type === "date") v = toISODate(v); obj[c.key] = v; });
        // Preserve the source serial number (S. No.) verbatim so the register shows the sheet's
        // real Sl No (incl. gaps/blanks) instead of a recomputed counter.
        if (serialIdx >= 0) obj.sl_no = String(r[serialIdx] == null ? "" : r[serialIdx]).trim();
        return obj;
      }).filter(o => columns.some(c => String(o[c.key] || "").trim() !== "") || (o.sl_no || "") !== "");
      // Stamp file order so rows display in the exact sequence they were imported.
      rows.forEach((o, i) => { o.seq = i + 1; });
      if (!rows.length) { window.alert("No data rows detected. Check the column order."); return; }
      if (!window.confirm("Import " + rows.length + " rows into " + title + "? (You can Edit them afterwards.)")) return;
      const res = await api.c4Bulk(coll, rows);
      await load();
      window.alert("Imported " + ((res && res.inserted) || rows.length) + " rows.");
    } catch (e) { window.alert("Import failed: " + ((e && e.message) || e)); }
  };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [people, setPeople] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filterVal, setFilterVal] = useState("");
  const [iqcParams, setIqcParams] = useState([]);
  // Keep rows in date sequence for tables that carry a date column (e.g. 7.7F-03, 7.7F-04).
  // ISO YYYY-MM-DD values from <input type="date"> sort chronologically as strings; undated rows sink to the bottom.
  const dateCol = columns.find(c => c.type === "date");
  // Order date-carrying registers chronologically (dates are stored ISO, so string compare works),
  // using seq as a stable tiebreaker within a day. This keeps entries date-wise and drops a newly
  // added entry directly after the latest one for its date. Undated rows sink to the bottom.
  const sortByDate = (arr) => {
    if (dateCol) {
      return [...arr].sort((a, b) => {
        const x = a[dateCol.key] || "", y = b[dateCol.key] || "";
        if (x !== y) { if (!x) return 1; if (!y) return -1; return x < y ? -1 : 1; }
        return (Number(a.seq) || 0) - (Number(b.seq) || 0);
      });
    }
    if (arr.some(x => x && x.seq != null && x.seq !== "")) {
      return [...arr].sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
    }
    return arr;
  };
  const load = () => (pageSize > 0
    ? api.c4Page(coll, page * pageSize, pageSize).then(r => { setItems(sortByDate(r.items || [])); setTotal(r.total || 0); })
    : api.c4List(coll).then(r => { setItems(sortByDate(r)); setTotal(r.length); }));
  useEffect(() => { load(); }, [coll, page, pageSize]);
  useEffect(() => { api.personnelPublic().then(setPeople).catch(() => {}); }, [coll]);
  useEffect(() => { if (columns.some(c => c.iqcActivity)) api.iqcScope().then(setIqcParams).catch(() => {}); }, [coll]);
  const iqcOptsFor = (c) => c.options || (c.iqcActivity ? iqcParams.filter(p => p.active !== false && p["applies_" + c.iqcActivity] !== false).map(p => p.name) : []);
  const startEdit = () => { setDraft(items.map(x => ({ ...x }))); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft([]); };
  const setCell = (i, k, v) => setDraft(draft.map((r, ri) => ri === i ? { ...r, [k]: v } : r));
  const setSign = (i, k, id) => { const g = people.find(x => x.id === id); setDraft(draft.map((r, ri) => ri === i ? { ...r, [k + "_id"]: id, [k]: g ? g.signature_image : "" } : r)); };
  const addFiles = (i, k, files) => { Array.from(files).forEach(async (file) => { try { const fd = new FormData(); fd.append("file", file); const ref = await api.uploadFile(fd); setDraft(d => d.map((row, ri) => ri === i ? { ...row, [k]: [...(row[k] || []), ref] } : row)); } catch (e) { alert("Upload failed: " + e.message); } }); };
  const removeFile = (i, k, fi) => setDraft(draft.map((row, ri) => ri === i ? { ...row, [k]: (row[k] || []).filter((_, x) => x !== fi) } : row));
  // Direct upload/remove from view mode (no Edit needed): upload to R2 and persist onto the row.
  const uploadRowFilesDirect = async (row, k, files) => {
    if (!row.id || !files || !files.length) return;
    try {
      // Upload to R2, then attach each ref via the "add"-scoped endpoint (works without edit rights).
      for (const file of Array.from(files)) { const fd = new FormData(); fd.append("file", file); const ref = await api.uploadFile(fd); await api.c4Attach(coll, row.id, k, ref); }
      load();
    } catch (e) { window.alert("Upload failed: " + ((e && e.message) || e)); }
  };
  const removeRowFileDirect = async (row, k, fi) => {
    if (!row.id) return;
    try { await api.c4Detach(coll, row.id, k, fi); load(); }
    catch (e) { window.alert("Remove failed: " + ((e && e.message) || e)); }
  };
  const addRow = () => setDraft([...draft, {}]);
  const removeRow = (i) => setDraft(draft.filter((_, ri) => ri !== i));
  const isBlankRow = (r) => !columns.some(c => { const v = r[c.key]; return Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && String(v).trim() !== ""); });
  const saveAll = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Every row is directly editable — approved rows are saved with the correction flag so
      // the backend amends them while preserving their signatures/approval + audit trail.
      // Approved rows are still never deleted (kept safe); they're just skipped on delete.
      let added = 0, deleted = 0;
      const draftIds = draft.filter(r => r.id).map(r => r.id);
      for (const it of items) { if (!draftIds.includes(it.id)) { if (it.approval_status === "approved") continue; await api.c4Del(coll, it.id); deleted++; } }
      for (const r of draft) {
        if (!r.id) { if (!isBlankRow(r)) { await api.c4Add(coll, r); added++; } }
        else {
          const orig = items.find(x => x.id === r.id);
          if (!orig || JSON.stringify(orig) !== JSON.stringify(r)) {
            const isApproved = orig && orig.approval_status === "approved";
            await api.c4Edit(coll, r.id, r, isApproved);   // isApproved -> correction flag (preserves signature)
          }
        }
      }
      setEditing(false); setDraft([]);
      // New rows sort to the END of the register (by date/seq), so with pagination they
      // land on the LAST page. Jump there so a freshly added entry is visible instead of
      // seeming to "disappear" from the page you were editing.
      if (added > 0 && pageSize > 0) {
        const lastPage = Math.max(0, Math.ceil((total + added - deleted) / pageSize) - 1);
        if (lastPage !== page) setPage(lastPage); else await load();
      } else {
        await load();
      }
    } catch (e) { window.alert("Save failed: " + ((e && e.message) || e)); }
    finally { setSaving(false); }
  };
  const hasApproval = !!APPROVAL_FLOW[coll];
  // "Signed by" (approval) column position: by default appended at the end; with approvalAfter it is
  // inserted right after that column key. apAfter is set only when the key exists in columns.
  const approvalCol = !editing && hasApproval;
  const apAfter = (approvalCol && approvalAfter && columns.some(c => c.key === approvalAfter)) ? approvalAfter : null;
  // "Signed by": pick a person from the personnel master and their stored signature is saved to the
  // row (add-scoped setfield) and shown in the cell. The formal Approve/Reopen workflow stays too.
  const setSignedImg = async (row, dataUrl) => {
    if (!row.id) return;
    try { await api.c4SetField(coll, row.id, "signed_signature", dataUrl || ""); load(); }
    catch (e) { window.alert("Sign failed: " + ((e && e.message) || e)); }
  };
  // Sign by picking a person — their saved signature auto-fills (no upload box).
  const approvalContent = (it) => <div className="signed-cell">
    {canAttach && it.id
      ? <PersonSignature compact people={people} value={it.signed_signature} onPick={(id, img) => setSignedImg(it, img)} />
      : (it.signed_signature && <img src={it.signed_signature} alt="signature" style={{ height: 28, maxWidth: 120, objectFit: "contain", display: "block", margin: "0 auto 2px" }} />)}
    <ApprovalBar coll={coll} rec={it} compact onChanged={load} />
  </div>;
  // Render a column label; "\n" in the label becomes a hard line break (stacked header text).
  const lbl = (t) => String(t).split("\n").map((line, li, arr) => li < arr.length - 1 ? <React.Fragment key={li}>{line}<br /></React.Fragment> : line);
  // Build the header. With headerGroups, a top row spans grouped leaf columns (e.g. "Requisition No." over Internal/External);
  // ungrouped columns and the S. No. / trailing cells rowSpan both rows so the header reads as one block.
  const groupOf = {};
  (headerGroups || []).forEach(g => g.cols.forEach(k => { groupOf[k] = g; }));
  const trailing = [];
  if (approvalCol && !apAfter) trailing.push(<th key="__signed" rowSpan={headerGroups ? 2 : 1} style={{ width: 132 }}>{apLabel}</th>);
  if (editing) trailing.push(<th key="__edit" className="noprint" rowSpan={headerGroups ? 2 : 1}></th>);
  const thead = headerGroups
    ? <thead>
        <tr>
          <th style={{ width: "8%" }} rowSpan={2}>S. No.</th>
          {columns.map(c => {
            const g = groupOf[c.key];
            if (!g) return <th key={c.key} rowSpan={2}>{lbl(c.label)}</th>;
            if (g.cols[0] !== c.key) return null; // covered by the group's colSpan cell
            return <th key={"grp-" + g.label} colSpan={g.cols.length} style={{ textAlign: "center" }}>{lbl(g.label)}</th>;
          })}
          {trailing}
        </tr>
        <tr>{columns.filter(c => groupOf[c.key]).map(c => <th key={c.key} style={{ textAlign: "center" }}>{lbl(c.label)}</th>)}</tr>
      </thead>
    : <thead><tr><th style={{ width: "8%" }}>S. No.</th>{columns.map(c => <React.Fragment key={c.key}><th>{lbl(c.label)}</th>{apAfter === c.key && <th style={{ width: 132 }}>{apLabel}</th>}</React.Fragment>)}{trailing}</tr></thead>;
  const base = pageSize > 0 ? page * pageSize : 0;
  const rowsToShow = editing ? draft : ((filter && filterVal) ? items.filter(r => (r[filter.key] || "") === filterVal) : items);
  // S. No. shown per row: existing rows show their stored sl_no; brand-new rows (no sl_no) continue
  // numbering from the highest sl_no on screen — so a new row reads e.g. 1064 after 1063, not a
  // stray page counter. Registers without any sl_no fall back to the running position (base + i + 1).
  const slNoDisplay = (() => {
    const hasSl = (r) => r && r.sl_no !== undefined && r.sl_no !== "" && !isNaN(parseInt(r.sl_no, 10));
    const nums = rowsToShow.filter(hasSl).map(r => parseInt(r.sl_no, 10));
    let running = nums.length ? Math.max(...nums) : base;
    return rowsToShow.map((r) => { if (hasSl(r)) return r.sl_no; running += 1; return String(running); });
  })();
  // Skeuomorphic blank rows: an empty register still looks like a paper form awaiting entries.
  const blanks = (!editing && total === 0 && rowsToShow.length === 0) ? [0, 1, 2] : [];
  return <div className="card">
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
      <b>{title} <span className="badge">{code}</span></b>
      <div className="row">
        {filter && !editing && <select value={filterVal} onChange={e => setFilterVal(e.target.value)} style={{ minWidth: 130 }}><option value="">All {filter.label}</option>{filter.options.map(o => <option key={o} value={o}>{o}</option>)}</select>}
        {!editing && canEdit && <button onClick={startEdit}>Edit</button>}
        {!editing && canAdd && <button className="noprint" onClick={() => importRef.current && importRef.current.click()} title="Bulk import rows from a CSV file">Import CSV</button>}
        <input ref={importRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={e => { importCSV(e.target.files[0]); e.target.value = ""; }} />
        {editing && <button className="primary" onClick={saveAll} disabled={saving}>{saving ? "Saving…" : "Save"}</button>}
        {editing && <button onClick={cancelEdit}>Cancel</button>}
        {editing && canAdd && <button onClick={addRow}>Add row</button>}
        {!editing && <button onClick={() => printFormat("fmt-" + coll)}>Print / PDF</button>}
      </div>
    </div>
    <div id={"fmt-" + coll} className="fmt-doc" style={{ overflowX: "auto" }}>
      <FormatHeader code={code} title={title} />
      <table className="fmt-table">
        {thead}
        <tbody>{rowsToShow.map((it, i) => <tr key={it.id || "new-" + i}><td>{slNoDisplay[i]}</td>{columns.map(c => <React.Fragment key={c.key}><td>{editing
          ? (c.type === "files"
            ? <div>{(it[c.key] || []).map((f, fi) => <div key={fi} style={{ fontSize: 12, marginBottom: 2 }}><a href={api.fileUrl(f)} target="_blank" rel="noreferrer">{f.name}</a> <button onClick={() => removeFile(i, c.key, fi)}>✕</button></div>)}<input type="file" multiple onChange={e => addFiles(i, c.key, e.target.files)} /></div>
            : c.type === "sign"
            ? (c.fromPersonnel
                ? <PersonSignature compact people={people} value={it[c.key]} valueId={it[c.key + "_id"]} onPick={(id) => setSign(i, c.key, id)} />
                : <div style={{ minWidth: 120 }}><SignatureInput compact value={it[c.key]} onChange={v => setCell(i, c.key, v)} /></div>)
            : c.type === "select"
            ? <select value={it[c.key] || ""} onChange={e => setCell(i, c.key, e.target.value)}><option value="">—</option>{iqcOptsFor(c).map(o => <option key={o} value={o}>{o}</option>)}</select>
            : c.type === "date"
            ? <input type="date" value={it[c.key] || ""} onChange={e => setCell(i, c.key, e.target.value)} />
            : <textarea rows={2} value={it[c.key] || ""} onChange={e => setCell(i, c.key, e.target.value)} style={{ minWidth: 110 }} />)
          : (c.type === "files" ? <div>{(it[c.key] || []).map((f, fi) => <div key={fi} className="row" style={{ fontSize: 12, gap: 4, alignItems: "center" }}><a href={api.fileUrl(f)} target="_blank" rel="noreferrer">{f.name}</a>{canAttach && <button className="noprint" onClick={() => removeRowFileDirect(it, c.key, fi)} style={{ padding: "0 5px" }}>✕</button>}</div>)}{canAttach && <label className="noprint" style={{ fontSize: 12, cursor: "pointer", color: "var(--primary, #2563eb)" }}>+ Upload<input type="file" multiple style={{ display: "none" }} onChange={e => uploadRowFilesDirect(it, c.key, e.target.files)} /></label>}</div>
            : c.type === "sign" ? (it[c.key] ? <img src={it[c.key]} alt="sig" style={{ height: 30 }} /> : "—") : inDate(it[c.key]))}</td>{apAfter === c.key && <td>{approvalContent(it)}</td>}</React.Fragment>)}{approvalCol && !apAfter && <td>{approvalContent(it)}</td>}{editing && <td className="noprint">{canDelete && <button onClick={() => removeRow(i)}>Delete</button>}</td>}</tr>)}{blanks.map(i => <tr key={"blank-" + i}><td>{i + 1}</td>{columns.map(c => <React.Fragment key={c.key}><td>&nbsp;</td>{apAfter === c.key && <td>&nbsp;</td>}</React.Fragment>)}{approvalCol && !apAfter && <td>&nbsp;</td>}</tr>)}</tbody>
      </table>
      <div className="fmt-footer">{footer}</div>
    </div>
    {!editing && pageSize > 0 && total > pageSize && <div className="row noprint" style={{ justifyContent: "space-between", marginTop: 8, alignItems: "center" }}>
      <span style={{ fontSize: 13, color: "var(--text-2)" }}>{total} records · page {page + 1} of {Math.max(1, Math.ceil(total / pageSize))} · printing prints the current page — switch to "All" to print everything</span>
      <div className="row" style={{ gap: 6 }}>
        <button disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Prev</button>
        <button disabled={(page + 1) * pageSize >= total} onClick={() => setPage(page + 1)}>Next ›</button>
        <select value={pageSize} onChange={e => { setPage(0); setPageSize(Number(e.target.value)); }} style={{ minWidth: 110 }}><option value={50}>50 / page</option><option value={100}>100 / page</option><option value={200}>200 / page</option><option value={0}>All</option></select>
      </div>
    </div>}
  </div>;
}

// ---------- GROUPED TEST PLAN & RECORD (SANBER LABS "Test Lab" report) ----------
// A single register whose rows are GROUPED under a parent Sl. No. Every group shares one
// Sample Code / Type of Test / Date of Testing (shown once via rowSpan, exactly like the paper
// form) and carries a DYNAMIC number of child result rows. Each child row independently holds
// the five result columns. One Mongo document == one Sl. No. group (with a `rows` array), so the
// generic c4 CRUD stores it unchanged — no backend model change.
//
// The nine visible columns never change (view AND edit render the same 9-column table so the
// structure always matches the reference): Sl. No. · Sample Code · Type of Test · Date of Testing
// · Test Result of 1st Person · Test Result of 2nd Person · Variation of Test Result ·
// Acceptance Criteria · Remarks. Row add/delete + group controls live in an unobtrusive, screen-only
// trailing cell (class "noprint") and inside the Sl. No. cell — they are not data columns.
const TPR_RESULT_COLS = [
  ["result1", "Test Result of 1st Person"],
  ["result2", "Test Result of 2nd Person"],
  ["variation", "Variation of Test Result"],
  ["acceptance", "Acceptance Criteria"],
  ["remarks", "Remarks"],
];
// Group-level (rowSpan) columns shown once per Sl. No. group. type "date" renders a date picker;
// anything else renders a wrapping textarea. Defaults reproduce the original Test Plan & Record;
// callers (e.g. Re-testing) can pass their own set to match a different paper form.
const TPR_GROUP_COLS = [
  { key: "sample_code", label: "Sample Code", type: "text", width: "11%" },
  { key: "type_of_test", label: "Type of Test", type: "text", width: "13%" },
  { key: "date_of_testing", label: "Date of Testing", type: "date", width: "10%" },
];

function GroupedTestPlanRecord({ code, title, coll, footer, groupCols, resultCols }) {
  const { can } = useAuth();
  const canEdit = can("clause_documents", "edit");
  const canAdd = can("clause_documents", "add");
  const canDelete = can("clause_documents", "delete");
  // Column definitions: default to the classic Test Plan & Record layout unless the caller
  // supplies its own (e.g. Re-testing has Old/New Sample Code, Date of Testing/Retesting, etc.).
  const gCols = (groupCols && groupCols.length) ? groupCols : TPR_GROUP_COLS;
  const rCols = (resultCols && resultCols.length) ? resultCols : TPR_RESULT_COLS;
  const blankRow = () => Object.fromEntries(rCols.map(c => [c[0], ""]));
  const blankGroup = () => ({ ...Object.fromEntries(gCols.map(c => [c.key, ""])), rows: [blankRow()] });
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  // ONE authorizing signature for the whole form (stored on the first group; kept in sync here).
  const [formSig, setFormSig] = useState("");
  const [formSigId, setFormSigId] = useState("");   // selected signer, to keep the picker in sync
  const [people, setPeople] = useState([]);   // to pick a person -> auto-fill their signature
  // Groups keep their entered order via `seq` (stamped on save); created_at is the tiebreaker.
  const sortBySeq = (arr) => [...arr].sort((a, b) => {
    const s = (Number(a.seq) || 0) - (Number(b.seq) || 0);
    return s !== 0 ? s : String(a.created_at || "").localeCompare(String(b.created_at || ""));
  });
  const load = () => api.c4List(coll).then(r => setItems(sortBySeq(r)));
  useEffect(() => { load(); }, [coll]);
  useEffect(() => { api.personnelPublic().then(setPeople).catch(() => {}); }, [coll]);

  // Enter edit with a deep copy so the on-screen draft never mutates the loaded records.
  const startEdit = () => { setDraft(items.map(g => ({ ...g, rows: ((g.rows && g.rows.length) ? g.rows : [blankRow()]).map(r => ({ ...r })) }))); const sg = items.find(g => g.signature) || {}; setFormSig(sg.signature || ""); setFormSigId(sg.signature_id || ""); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft([]); };
  const viewSignature = (items.find(g => g.signature) || {}).signature || "";

  // --- Group-level edits (parent Sl. No.) ---
  const setGroup = (gi, k, v) => setDraft(draft.map((g, i) => i === gi ? { ...g, [k]: v } : g));
  const addGroup = () => setDraft([...draft, blankGroup()]);
  const removeGroup = (gi) => setDraft(draft.filter((_, i) => i !== gi));

  // --- Child-row edits (SCOPED to one group, so "+ Add Row" on Sl. No. 1 never touches Sl. No. 2) ---
  const setRow = (gi, ri, k, v) => setDraft(draft.map((g, i) => i === gi ? { ...g, rows: g.rows.map((r, j) => j === ri ? { ...r, [k]: v } : r) } : g));
  const addRow = (gi) => setDraft(draft.map((g, i) => i === gi ? { ...g, rows: [...(g.rows || []), blankRow()] } : g));
  const removeRow = (gi, ri) => setDraft(draft.map((g, i) => i === gi ? { ...g, rows: (g.rows || []).filter((_, j) => j !== ri) } : g));
  // Delete the WHOLE page/record (a Sl. No. group), not a single row. Confirms first, reuses
  // the existing c4Del API, and removes it from both items + draft so Save can't re-hit it.
  const deleteGroup = async (gi) => {
    const g = draft[gi];
    if (!window.confirm("Are you sure you want to delete this page?\n\nThis action cannot be undone.")) return;
    if (g && g.id) {
      try { await api.c4Del(coll, g.id); setItems(prev => prev.filter(x => x.id !== g.id)); }
      catch (e) { window.alert("Delete failed: " + ((e && e.message) || e)); return; }
    }
    removeGroup(gi);
  };

  const isBlankGroup = (g) => gCols.every(c => !String(g[c.key] || "").trim()) &&
    (g.rows || []).every(r => rCols.every(c => !String(r[c[0]] || "").trim()));

  const saveAll = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Delete groups removed on screen; add brand-new non-blank groups; update changed ones.
      // Each group persists its child `rows` array verbatim, so counts stay exactly as entered.
      const draftIds = draft.filter(g => g.id).map(g => g.id);
      for (const it of items) { if (!draftIds.includes(it.id)) await api.c4Del(coll, it.id); }
      for (let i = 0; i < draft.length; i++) {
        const src = draft[i];
        const g = { ...src, seq: i + 1, rows: (src.rows || []).map(r => ({ ...r })) };
        if (!g.rows.length) g.rows = [blankRow()];
        // One form-level signature: the first group carries it (+ the signer id); rest cleared.
        g.signature = (i === 0 ? formSig : "");
        g.signature_id = (i === 0 ? formSigId : "");
        if (!g.id) { if (!isBlankGroup(g)) await api.c4Add(coll, g); }
        else { const orig = items.find(x => x.id === g.id); if (!orig || JSON.stringify(orig) !== JSON.stringify(g)) await api.c4Edit(coll, g.id, g); }
      }
      setEditing(false); setDraft([]);
      await load();
    } catch (e) { window.alert("Save failed: " + ((e && e.message) || e)); }
    finally { setSaving(false); }
  };

  const groups = editing ? draft : items;
  // When empty and not editing, show three skeleton groups so the register still reads as a paper form.
  const showBlanks = !editing && groups.length === 0;
  const viewGroups = showBlanks ? [blankGroup(), blankGroup(), blankGroup()] : groups;

  return <div className="card">
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
      <b>{title} <span className="badge">{code}</span></b>
      <div className="row">
        {!editing && canEdit && <button onClick={startEdit}>Edit</button>}
        {editing && <button className="primary" onClick={saveAll} disabled={saving}>{saving ? "Saving…" : "Save"}</button>}
        {editing && <button onClick={cancelEdit}>Cancel</button>}
        {editing && canAdd && <button onClick={addGroup}>+ Add Sl. No.</button>}
        {!editing && <button onClick={() => printFormat("fmt-" + coll)}>Print / PDF</button>}
      </div>
    </div>
    <div id={"fmt-" + coll} className="fmt-doc" style={{ overflowX: "auto" }}>
      <FormatHeader code={code} title={title} />
      <table className="fmt-table">
        <thead><tr>
          <th style={{ width: "6%" }}>Sl. No.</th>
          {gCols.map(c => <th key={c.key} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}
          {rCols.map(c => <th key={c[0]}>{c[1]}</th>)}
          {editing && <th className="noprint" style={{ width: 40 }}></th>}
        </tr></thead>
        <tbody>
          {viewGroups.map((g, gi) => {
            const rows = (g.rows && g.rows.length) ? g.rows : [blankRow()];
            const cellView = { verticalAlign: "top", whiteSpace: "pre-wrap", wordBreak: "break-word" };
            const ta = { minWidth: 130, width: "100%", minHeight: 60, resize: "vertical" };   // larger, wrapping fields
            const out = rows.map((r, ri) => <tr key={(g.id || "new-" + gi) + "-" + ri}>
              {ri === 0 && <>
                <td rowSpan={rows.length} style={{ verticalAlign: "top", textAlign: "center", fontWeight: 600 }}>
                  {gi + 1}
                  {editing && <div className="noprint" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                    <button onClick={() => addRow(gi)} style={{ fontSize: 11, padding: "2px 6px" }}>+ Add Row</button>
                    {canDelete && <button onClick={() => deleteGroup(gi)} title="Delete this whole page/record" style={{ fontSize: 11, padding: "2px 6px", color: "#fff", background: "var(--danger)", borderColor: "var(--danger)" }}>🗑 Delete Record</button>}
                  </div>}
                </td>
                {gCols.map(c => <td key={c.key} rowSpan={rows.length} style={c.type === "date" ? { verticalAlign: "top" } : cellView}>{editing
                  ? (c.type === "date"
                      ? <input type="date" value={g[c.key] || ""} onChange={e => setGroup(gi, c.key, e.target.value)} />
                      : <textarea rows={3} value={g[c.key] || ""} onChange={e => setGroup(gi, c.key, e.target.value)} style={ta} />)
                  : (c.type === "date" ? inDate(g[c.key]) : (g[c.key] || ""))}</td>)}
              </>}
              {rCols.map(c => <td key={c[0]} style={cellView}>{editing
                ? <textarea rows={3} value={r[c[0]] || ""} onChange={e => setRow(gi, ri, c[0], e.target.value)} style={ta} />
                : (r[c[0]] || "")}</td>)}
              {editing && <td className="noprint" style={{ textAlign: "center" }}>
                {canDelete && rows.length > 1 && <button onClick={() => removeRow(gi, ri)} title="Delete this row">✕</button>}
              </td>}
            </tr>);
            return out;
          })}
        </tbody>
      </table>
      {editing && canAdd && <button onClick={addGroup} className="noprint" style={{ marginTop: 10 }}>+ Add Sl. No. group</button>}
      {/* ONE authorizing signature for the whole form. Pick a person from Masters and their
          stored signature auto-fills (exactly like the Plan sheet) — no upload box. */}
      <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
        <div style={{ textAlign: "center", minWidth: 240 }}>
          <b style={{ fontSize: 13, display: "block", marginBottom: 6 }}>Signature of authorizing person</b>
          {editing
            ? <PersonSignature people={people} value={formSig} valueId={formSigId} onPick={(id, img) => { setFormSigId(id); setFormSig(img); }} minWidth={240} />
            : (viewSignature ? <img src={viewSignature} alt="signature" style={{ height: 52, maxWidth: 240, objectFit: "contain", display: "block", margin: "0 auto" }} /> : <div className="sign-line" style={{ borderTop: "1px solid #666", width: 200, height: 34, margin: "0 auto" }} />)}
        </div>
      </div>
      <div className="fmt-footer">{footer}</div>
    </div>
  </div>;
}

function FormatDoc({ code, title, coll, headerFields, showDate, clauses, signatures }) {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [signs, setSigns] = useState([]);
  const [people, setPeople] = useState([]);
  const load = () => api.c4List(coll).then(setItems);
  useEffect(() => { load(); api.signatoriesPublic().then(setSigns).catch(() => {}); api.personnelPublic().then(setPeople).catch(() => {}); }, [coll]);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const fileB64 = (file, k) => { const r = new FileReader(); r.onload = () => setF(k, r.result); r.readAsDataURL(file); };
  const save = async () => { if (form.id) await api.c4Edit(coll, form.id, form); else await api.c4Add(coll, form); setForm(null); load(); };
  const del = async (id) => { if (window.confirm("Delete this record?")) { await api.c4Del(coll, id); load(); } };
  const eye = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>;
  const docBody = (rec) => <div id={"docview-" + coll} className="fmt-doc">
    <FormatHeader code={code} title={title} />
    {showDate && <div style={{ textAlign: "right", marginBottom: 8 }}>Date: {inDate(rec.doc_date) || ""}</div>}
    {headerFields.map(f => <div key={f.key} style={{ marginBottom: 6 }}><b>{f.label}:</b> {rec[f.key] || ""}</div>)}
    <ol style={{ lineHeight: 1.7, marginTop: 12 }}>{clauses.map((c, i) => <li key={i} style={{ marginBottom: 8 }}>{c}</li>)}</ol>
    <div style={{ marginTop: 34 }}>{signatures.map((s, i) => <div key={i} className="fmt-sign" style={{ display: "inline-block", width: "45%", verticalAlign: "top" }}>
      {rec[s.key] ? <img src={rec[s.key]} alt="signature" style={{ height: 44 }} /> : <div className="sign-line" style={{ borderTop: "1px solid #666", width: 190, height: 38, marginBottom: 4 }}></div>}
      <div>{s.label}</div>
    </div>)}</div>
  </div>;
  return <div className="card">
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
      <b>{title} <span className="badge">{code}</span></b>
      {can("clause_documents", "add") && !form && <button className="primary" onClick={() => setForm({})}>New</button>}
    </div>
    {form && <div style={{ marginBottom: 10 }}>
      {showDate && <div><label>Date</label><input type="date" value={form.doc_date || ""} onChange={e => setF("doc_date", e.target.value)} style={{ maxWidth: 240 }} /></div>}
      {headerFields.map(f => <div key={f.key}><label>{f.label}</label><input value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} style={{ maxWidth: 420 }} /></div>)}
      {signatures.map(s => <div key={s.key}>{s.fromPersonnel
        ? <><label>{s.label} — from Personnel</label><select value={form[s.key + "_id"] || ""} onChange={e => { const g = people.find(x => x.id === e.target.value); const upd = { ...form, [s.key + "_id"]: e.target.value, [s.key]: g ? g.signature_image : "" }; if (s.nameKey) upd[s.nameKey] = g ? g.name : ""; setForm(upd); }} style={{ maxWidth: 360 }}><option value="">— select personnel —</option>{people.map(g => <option key={g.id} value={g.id}>{g.name}{g.designation ? " (" + g.designation + ")" : ""}</option>)}</select><div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 4 }}>{form[s.key] ? "signature loaded from master ✓" : "— (none on file)"}</div></>
        : s.fromSignatories
        ? <><label>{s.label} — signature</label><select value={form[s.key + "_id"] || ""} onChange={e => { const g = signs.find(x => x.id === e.target.value); setForm({ ...form, [s.key + "_id"]: e.target.value, [s.key]: g ? g.signature_image : "", [s.key + "_role"]: g ? g.role : "" }); }} style={{ maxWidth: 360 }}><option value="">— select from Authorized Signatories —</option>{signs.map(g => <option key={g.id} value={g.id}>{g.role}</option>)}</select><div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 4 }}>{form[s.key] ? "signature loaded from master ✓" : "— (none on file)"}</div></>
        : <><label>{s.label} — signature image</label><input type="file" accept="image/*" onChange={e => fileB64(e.target.files[0], s.key)} />{form[s.key] && <img src={form[s.key]} alt="sig" style={{ height: 36, marginTop: 4 }} />}</>}</div>)}
      <div className="row" style={{ marginTop: 10 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setForm(null)}>Cancel</button></div>
    </div>}
    <table style={{ marginTop: 6 }}><thead><tr>{headerFields.slice(0, 2).map(f => <th key={f.key}>{f.label}</th>)}{showDate && <th>Date</th>}<th></th></tr></thead>
      <tbody>{items.map(it => <tr key={it.id}>{headerFields.slice(0, 2).map(f => <td key={f.key}>{it[f.key]}</td>)}{showDate && <td>{inDate(it.doc_date)}</td>}
        <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
          <button className="view-btn" title="View" aria-label="View" onClick={() => setView(it)}>{eye}</button>
          {can("clause_documents", "edit") && <button onClick={() => setForm(it)} style={{ marginLeft: 8 }}>Edit</button>}
          {can("clause_documents", "delete") && <button onClick={() => del(it.id)} style={{ marginLeft: 8 }}>Delete</button>}
        </td></tr>)}</tbody></table>
    {view && <div className="modal-overlay" onClick={() => setView(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
          <b>{title}</b>
          <div className="row"><button onClick={() => printFormat("docview-" + coll)}>Print / PDF</button><button onClick={() => setView(null)}>Close</button></div>
        </div>
        <ApprovalBar coll={coll} rec={view} onChanged={() => { setView(null); load(); }} />
        {docBody(view)}
      </div>
    </div>}
  </div>;
}

function FormatFormDoc({ code, title, coll, fields, intro, innerTable, signatures, formFirst, userFillable, notes, postFields }) {
  const { can } = useAuth();
  // formFirst: show the blank paper form directly (ready to fill) instead of behind a "New" button.
  // userFillable: let any signed-in user add/edit/delete entries regardless of role permissions.
  const canAdd = userFillable || can("clause_documents", "add");
  const canEdit = userFillable || can("clause_documents", "edit");
  const canDelete = userFillable || can("clause_documents", "delete");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(formFirst ? {} : null);
  const [rows, setRows] = useState([]);
  const [rowDraft, setRowDraft] = useState({});
  const [view, setView] = useState(null);
  const [signs, setSigns] = useState([]);
  const [people, setPeople] = useState([]);
  const load = () => api.c4List(coll).then(setItems);
  useEffect(() => { load(); api.signatoriesPublic().then(setSigns).catch(() => {}); api.personnelPublic().then(setPeople).catch(() => {}); }, [coll]);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const fileB64 = (file, k) => { const r = new FileReader(); r.onload = () => setF(k, r.result); r.readAsDataURL(file); };
  const startNew = () => { setForm({}); setRows([]); setRowDraft({}); };
  const startEdit = (rec) => { setForm(rec); setRows(rec.rows || []); setRowDraft({}); };
  const addRow = () => { setRows([...rows, rowDraft]); setRowDraft({}); };
  const save = async () => { if (form.id) await api.c4Edit(coll, form.id, { ...form, rows }); else await api.c4Add(coll, { ...form, rows }); setForm(formFirst ? {} : null); setRows([]); setRowDraft({}); load(); };
  const resetForm = () => { setForm(formFirst ? {} : null); setRows([]); setRowDraft({}); };
  const del = async (id) => { if (window.confirm("Delete this record?")) { await api.c4Del(coll, id); load(); } };
  const eye = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>;
  const docBody = (rec) => <div id={"docview-" + coll} className="fmt-doc">
    <FormatHeader code={code} title={title} />
    {fields.map(f => <div key={f.key} style={{ marginBottom: 6 }}>
      <b>{f.label}:</b> {f.type === "list" ? <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>{(rec[f.key] || "").split("\n").filter(x => x.trim()).map((li, li_i) => <li key={li_i} style={{ marginBottom: 2 }}>{li}</li>)}</ul> : f.type === "textarea" ? <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{rec[f.key] || ""}</div> : <span> {rec[f.key] || ""}</span>}
    </div>)}
    {intro && <p style={{ marginTop: 10 }}>{intro}</p>}
    {innerTable && <table className="fmt-table" style={{ marginTop: 10 }}>
      <thead><tr><th style={{ width: "8%" }}>Sl. No.</th>{innerTable.columns.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
      <tbody>{(rec.rows || []).map((r, i) => <tr key={i}><td>{i + 1}</td>{innerTable.columns.map(c => <td key={c.key}>{r[c.key]}</td>)}</tr>)}</tbody>
    </table>}
    {postFields && postFields.map(f => <div key={f.key} style={{ marginTop: 10 }}>
      <b>{f.label}:</b> {f.type === "textarea" ? <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{rec[f.key] || ""}</div> : <span> {rec[f.key] || ""}</span>}
    </div>)}
    {notes && notes.length > 0 && <div style={{ marginTop: 14, fontSize: 13, lineHeight: 1.5 }}>{notes.map((ln, i) => <div key={i} style={{ marginBottom: 4, fontWeight: /—$/.test(ln) ? 600 : 400 }}>{ln}</div>)}</div>}
    {signatures && signatures.length > 0 && <div style={{ marginTop: 34 }}>{signatures.map((s, i) => <div key={i} className="fmt-sign" style={{ display: "inline-block", width: "45%", verticalAlign: "top" }}>
      {rec[s.key] ? <img src={rec[s.key]} alt="signature" style={{ height: 44 }} /> : <div className="sign-line" style={{ borderTop: "1px solid #666", width: 190, height: 38, marginBottom: 4 }}></div>}
      <div>{rec[s.key + "_label"] || s.label}</div>
    </div>)}</div>}
  </div>;
  return <div className="card">
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
      <b>{title} <span className="badge">{code}</span></b>
      {canAdd && !form && <button className="primary" onClick={startNew}>New</button>}
    </div>
    {form && <div style={{ marginBottom: 10 }}>
      {fields.map(f => <div key={f.key}><label>{f.label}{f.type === "list" ? " (one item per line)" : ""}</label>{(f.type === "textarea" || f.type === "list")
        ? <textarea rows={f.type === "list" ? 6 : 4} value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} />
        : <input type={f.type === "date" ? "date" : "text"} value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} style={{ maxWidth: 420 }} />}</div>)}
      {innerTable && <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 8 }}>
        <b>{innerTable.label}</b>
        {rows.map((r, i) => <div key={i} className="row" style={{ marginTop: 6, flexWrap: "wrap", alignItems: "center", gap: 6 }}>
          <span style={{ color: "var(--text-2)", fontSize: 13 }}>{i + 1}.</span>
          {innerTable.columns.map(c => <input key={c.key} placeholder={c.label} value={r[c.key] || ""} onChange={e => setRows(rows.map((x, xi) => xi === i ? { ...x, [c.key]: e.target.value } : x))} style={{ maxWidth: 210 }} />)}
          <button onClick={() => setRows(rows.filter((_, xi) => xi !== i))}>Remove</button>
        </div>)}
        <div className="row" style={{ marginTop: 6, flexWrap: "wrap", gap: 6 }}>
          {innerTable.columns.map(c => <input key={c.key} placeholder={c.label} value={rowDraft[c.key] || ""} onChange={e => setRowDraft({ ...rowDraft, [c.key]: e.target.value })} style={{ maxWidth: 210 }} />)}
          <button onClick={addRow}>Add line</button>
        </div>
      </div>}
      {postFields && postFields.map(f => <div key={f.key}><label>{f.label}</label>{f.type === "textarea"
        ? <textarea rows={3} value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} />
        : <input value={form[f.key] || ""} onChange={e => setF(f.key, e.target.value)} style={{ maxWidth: 420 }} />}</div>)}
      {signatures && signatures.map(s => <div key={s.key}><label>{s.label} — role (printed under the signature)</label><input value={form[s.key + "_label"] || ""} onChange={e => setF(s.key + "_label", e.target.value)} placeholder={s.label} style={{ maxWidth: 360 }} />{s.fromPersonnel ? <><label>{s.label} — pull signature from Personnel</label><select value={form[s.key + "_id"] || ""} onChange={e => { const g = people.find(x => x.id === e.target.value); setForm({ ...form, [s.key + "_id"]: e.target.value, [s.key]: g ? g.signature_image : "" }); }} style={{ maxWidth: 360 }}><option value="">— select personnel —</option>{people.map(g => <option key={g.id} value={g.id}>{g.name}{g.designation ? " (" + g.designation + ")" : ""}</option>)}</select></> : s.fromSignatories ? <><label>{s.label} — pull signature from Authorized Signatories</label><select value={form[s.key + "_id"] || ""} onChange={e => { const g = signs.find(x => x.id === e.target.value); setForm({ ...form, [s.key + "_id"]: e.target.value, [s.key]: g ? g.signature_image : "" }); }} style={{ maxWidth: 360 }}><option value="">— select —</option>{signs.map(g => <option key={g.id} value={g.id}>{g.role}</option>)}</select></> : <><label>{s.label} — signature image</label><input type="file" accept="image/*" onChange={e => fileB64(e.target.files[0], s.key)} /></>}{form[s.key] && <img src={form[s.key]} alt="sig" style={{ height: 36, marginTop: 4, display: "block" }} />}</div>)}
      <div className="row" style={{ marginTop: 10 }}><button className="primary" onClick={save}>Save</button><button onClick={resetForm}>{formFirst ? "Clear" : "Cancel"}</button></div>
    </div>}
    {formFirst && items.length > 0 && <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 10 }}><b>Saved records</b></div>}
    <table style={{ marginTop: 6 }}><thead><tr>{fields.slice(0, 2).map(f => <th key={f.key}>{f.label}</th>)}<th></th></tr></thead>
      <tbody>{items.map(it => <tr key={it.id}>{fields.slice(0, 2).map(f => <td key={f.key}>{it[f.key]}</td>)}
        <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
          <button className="view-btn" title="View" aria-label="View" onClick={() => setView(it)}>{eye}</button>
          {canEdit && <button onClick={() => startEdit(it)} style={{ marginLeft: 8 }}>Edit</button>}
          {canDelete && <button onClick={() => del(it.id)} style={{ marginLeft: 8 }}>Delete</button>}
        </td></tr>)}</tbody></table>
    {view && <div className="modal-overlay" onClick={() => setView(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
          <b>{title}</b>
          <div className="row"><button onClick={() => printFormat("docview-" + coll)}>Print / PDF</button><button onClick={() => setView(null)}>Close</button></div>
        </div>
        <ApprovalBar coll={coll} rec={view} onChanged={() => { setView(null); load(); }} />
        {docBody(view)}
      </div>
    </div>}
  </div>;
}

// Approval routing per the Quality / Format Manual. Must mirror backend APPROVAL_FLOW.
export const APPROVAL_FLOW = {
  ilc_plan: ["tm", "qm"], amended_technical_records: ["tm", "qm"], mrm_minutes: ["qm", "ceo"],
  observation_sheet: ["tm"], mu_evaluation: ["tm"], replicate_testing: ["tm"],
  retesting_retained: ["tm"], blind_samples: ["tm"], functional_check_record: ["tm"],
  review_reported_results: ["tm"], equipment_logbook: ["tm"],
  service_verification: ["qm"], nonconforming_work: ["qm"], complaint_resolution: ["qm"],
  equipment_calibration: ["qmtm"], job_card: ["qmtm"],
};
const STAGE_LABELS = { tm: "Technical Manager", qm: "Quality Manager", qmtm: "QM / TM", ceo: "CEO" };

function ApprovalBar({ coll, rec, compact, onChanged }) {
  const { can } = useAuth();
  const flow = APPROVAL_FLOW[coll];
  if (!flow || !rec || !rec.id) return null;
  const doneCount = flow.filter(s => rec["approved_" + s + "_at"]).length;
  const approve = async (stage) => {
    if (!window.confirm("Sign off this record as " + STAGE_LABELS[stage] + "? Your saved signature is applied; the record locks when fully approved.")) return;
    try { await api.c4Approve(coll, rec.id, stage); onChanged && onChanged(); } catch (e) { alert(e.message); }
  };
  const reopen = async () => {
    if (!window.confirm("Reopen this record? All approvals are cleared and the action is logged.")) return;
    try { await api.c4Reopen(coll, rec.id); onChanged && onChanged(); } catch (e) { alert(e.message); }
  };
  return <div className={compact ? "" : "row"} style={compact ? { fontSize: 12 } : { flexWrap: "wrap", gap: 10, alignItems: "center", margin: "8px 0", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8 }}>
    {!compact && <b style={{ fontSize: 13 }}>Approval{rec.approval_status === "approved" ? " — approved & locked" : ""}</b>}
    {flow.map((s, i) => {
      const at = rec["approved_" + s + "_at"];
      if (at) { const sig = rec["approved_" + s + "_signature"]; return <span key={s} style={{ display: "inline-flex", flexDirection: "column", alignItems: compact ? "flex-start" : "center", gap: 2, color: "var(--success)" }}>{sig ? <img src={sig} alt="signature" style={{ height: compact ? 30 : 40, maxWidth: 160, objectFit: "contain" }} /> : <span>✓</span>}<span style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.2 }}>{STAGE_LABELS[s]}{compact ? "" : " · " + (rec["approved_" + s + "_by"] || "")} · {inDate(String(at).slice(0, 10))}</span></span>; }
      const prevDone = flow.slice(0, i).every(x => rec["approved_" + x + "_at"]);
      if (prevDone && can("clause_documents", "approve")) return <button key={s} type="button" className="noprint" onClick={() => approve(s)} style={compact ? { fontSize: 11, padding: "2px 8px", minHeight: 0 } : {}}>Approve ({STAGE_LABELS[s]})</button>;
      return <span key={s} style={{ color: "var(--text-3)" }}>… {STAGE_LABELS[s]}</span>;
    })}
    {doneCount > 0 && can("clause_documents", "approve") && <button type="button" className="noprint" onClick={reopen} style={compact ? { fontSize: 11, padding: "2px 8px", minHeight: 0 } : { color: "var(--danger)" }}>Reopen</button>}
  </div>;
}

function FormatTopbar({ n, only }) {
  const fmts = CLAUSE_FORMATS[String(n)] || [];
  const f = fmts.find(x => x.coll === only) || {};
  return <div className="topbar" style={{ display: "block" }}>
    <NavLink to={"/clause/" + n} style={{ fontSize: 13, textDecoration: "none", color: "var(--text-2)" }}>← Clause {n} — {CLAUSE_TITLES[String(n)] || ""}</NavLink>
    <h2 style={{ marginTop: 4 }}>{f.section ? f.section + " · " : ""}{f.code ? f.code + " — " : ""}{f.title || ("Clause " + n)}</h2>
  </div>;
}

// ---- Person-based signature: the ONE canonical "sign by picking a person" control ----
// Reused everywhere a person signs a document. Selecting a person from Masters → Personnel
// auto-fills their ALREADY-SAVED signature — no re-upload, no drag-and-drop box. If the chosen
// person has no signature on file, a clean "Signature not available for this person" note shows
// instead of an upload box. (Masters, where signatures are actually stored, still use SignatureInput.)
//   value    – current signature image (base64) shown above the picker
//   valueId  – currently-selected person id (keeps the dropdown in sync; optional)
//   people   – personnel list from api.personnelPublic()
//   onPick(id, signatureImage) – called when a person is chosen (or cleared)
function PersonSignature({ value, valueId, people, onPick, label, compact, minWidth }) {
  const [checkedId, setCheckedId] = useState("");
  const pick = (id) => {
    setCheckedId(id || "");
    if (!id) { onPick("", ""); return; }
    const p = (people || []).find(x => x.id === id);
    onPick(id, p ? (p.signature_image || "") : "");
  };
  // Only show "not available" right after the user picks a person who has no signature on file
  // (guarded so navigating between saved records doesn't show a stale message).
  const showNoSig = !value && checkedId !== "" && (valueId == null || valueId === checkedId);
  const h = compact ? 30 : 46;
  return <div style={{ textAlign: "center", minWidth: minWidth || (compact ? 120 : 160) }}>
    {value
      ? <img src={value} alt={label || "signature"} style={{ height: h, maxWidth: compact ? 130 : 200, objectFit: "contain", display: "block", margin: "0 auto 2px" }} />
      : (showNoSig
          ? <div className="noprint" style={{ minHeight: h, fontSize: 11, color: "var(--text-3)", display: "flex", alignItems: "center", justifyContent: "center" }}>Signature not available for this person</div>
          : <div style={{ height: h }} />)}
    <select className="noprint" value={valueId || ""} onChange={e => pick(e.target.value)} style={{ fontSize: compact ? 11 : 12, maxWidth: compact ? 140 : 200, marginBottom: 2 }}>
      <option value="">— select person —</option>
      {(people || []).map(p => <option key={p.id} value={p.id}>{p.name}{p.designation ? " (" + p.designation + ")" : ""}</option>)}
    </select>
    {label && <div style={{ borderTop: "1px solid var(--text)", paddingTop: 2, fontSize: 13 }}>{label}</div>}
  </div>;
}

function SignatureInput({ value, onChange, compact }) {
  const [drag, setDrag] = useState(false);
  const inputRef = React.useRef(null);
  const handleFile = (file) => { if (!file) return; const r = new FileReader(); r.onload = () => onChange(r.result); r.readAsDataURL(file); };
  if (compact) return <div style={{ maxWidth: 140 }}>
    {/* the signature image prints (shown once); controls below are screen-only */}
    {value && <img src={value} alt="signature" style={{ height: 30, maxWidth: 130, objectFit: "contain", display: "block", margin: "0 auto 2px" }} />}
    <div className="noprint">
      {value
        ? <div className="row" style={{ gap: 4, justifyContent: "center" }}>
          <button type="button" onClick={() => inputRef.current && inputRef.current.click()} style={{ fontSize: 11 }}>Replace</button>
          <button type="button" onClick={() => onChange("")} style={{ fontSize: 11, color: "var(--danger)" }}>✕</button>
        </div>
        : <div onClick={() => inputRef.current && inputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
          style={{ border: "1.5px dashed " + (drag ? "var(--brand)" : "var(--border-strong)"), borderRadius: 6, padding: "8px 6px", textAlign: "center", cursor: "pointer", color: "var(--text-3)", fontSize: 11, background: drag ? "var(--focus)" : "transparent" }}>
          Drop sign<br />or click
        </div>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
    </div>
  </div>;
  return <div>
    {value
      ? <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 6, border: "1px solid var(--border)", borderRadius: 8, padding: 10, background: "var(--bg)" }}>
        <img src={value} alt="signature" style={{ height: 60, maxWidth: 260, objectFit: "contain" }} />
        <div className="row" style={{ gap: 6 }}>
          <button type="button" onClick={() => inputRef.current && inputRef.current.click()}>Replace</button>
          <button type="button" onClick={() => onChange("")} style={{ color: "var(--danger)" }}>Remove</button>
        </div>
      </div>
      : <div onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        style={{ border: "1.5px dashed " + (drag ? "var(--brand)" : "var(--border-strong)"), borderRadius: 8, padding: "18px 16px", textAlign: "center", cursor: "pointer", color: "var(--text-2)", background: drag ? "var(--focus)" : "transparent", maxWidth: 320 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 4 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></svg>
        <div style={{ fontSize: 13 }}>Click to upload signature<br /><span style={{ fontSize: 12, color: "var(--text-3)" }}>or drag &amp; drop an image</span></div>
      </div>}
    <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
  </div>;
}

function ActiveToggle({ value, onChange }) {
  const on = value !== false;
  const seg = (label, sel, val, color) => <button type="button" onClick={() => onChange(val)} style={{ minHeight: 0, padding: "6px 16px", border: "1px solid var(--border-strong)", background: sel ? color : "var(--surface)", color: sel ? "#fff" : "var(--text-2)", fontWeight: sel ? 600 : 400, cursor: "pointer" }}>{label}</button>;
  return <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", marginTop: 4 }}>
    {seg("Active", on, true, "var(--success)")}
    {seg("Inactive", !on, false, "var(--danger)")}
  </div>;
}


export { printFormat, printContent, printPages, PagedDoc, FixedPagesDoc, FormatHeader, RecordSection, FormatTable, GroupedTestPlanRecord, FormatDoc, FormatFormDoc, FormatTopbar, SignatureInput, PersonSignature, ActiveToggle, ApprovalBar };
